import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule, Validators, FormArray, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PropertiesService } from '../../../../core/services/properties.service';
import { Property, PropertyImage } from '../../../../core/models/property.model';
import { PropertyUploadService, LookupRecord } from '../../../../core/services/property-upload.service';
import { CurrencyService } from '../../../../core/services/currency.service';
import { LucideIconsModule } from '../../../../shared/lucide-icons.module';
import { NotificationService } from '../../../../services/notification.service';
import { ConfirmDialogService } from '../../../../shared/components/confirm-dialog/confirm-dialog.service';
import { debounceTime, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { Project, Phase } from '../../../../core/models/project.model';
import { COUNTRIES } from '../../../../shared/data/countries';
import { ExternalViewerDialogService } from '../../../../shared/components/external-viewer-dialog/external-viewer-dialog.service';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { buildWorkspaceRouteUrl, MediaLibraryWorkspace, openMediaLibraryPopup } from '../../../../shared/utils/media-library-popup.util';
import { hasPropertyCreatePermission, permissionSetFromRaw } from '../../../../shared/utils/permissions.util';
import { DeveloperPropertyEditFormComponent } from './components/developer-property-edit-form/developer-property-edit-form.component';
import { DeveloperPropertyEditStepperComponent } from './components/developer-property-edit-stepper/developer-property-edit-stepper.component';

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
    QA: 'QAR',
    AE: 'AED',
    SA: 'SAR',
    US: 'USD',
    GB: 'GBP',
    EG: 'EGP',
    FR: 'EUR',
    DE: 'EUR',
    IT: 'EUR',
    ES: 'EUR',
    CA: 'USD',
    AU: 'USD',
    PK: 'PKR',
    IN: 'INR',
    BD: 'BDT',
    TR: 'TRY',
    KW: 'KWD',
    BH: 'BHD',
    OM: 'OMR',
    JO: 'JOD'
};

const LANDMARK_PRESETS = ['Mall', 'School', 'University', 'Hospital', 'Clinic', 'Metro Station', 'Airport', 'Park', 'Beach', 'Downtown'];

type DetailTabId = 'features' | 'landmarks' | 'plans' | 'images';
type PropertyTypeSelectOption = { label: string; value: string; disabled?: boolean };
type PropertyEditDraftV1 = {
    version: 1;
    propertyId: string;
    updatedAt: string;
    formValue: Record<string, unknown>;
    selectedFeatures: Array<[string, string]>;
    landmarks: Array<Record<string, unknown>>;
    paymentPlans: Array<Record<string, unknown>>;
    planMode: 'manual' | 'auto' | 'none';
    publicPriceSource?: string;
    images: PropertyImage[];
};

@Component({
    selector: 'app-developer-property-edit',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterLink, LucideIconsModule, DeveloperPropertyEditStepperComponent, DeveloperPropertyEditFormComponent],
    templateUrl: './developer-property-edit.component.html',
    styleUrls: ['./developer-property-edit.component.scss']
})
export class DeveloperPropertyEditComponent implements OnInit, OnDestroy {
    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private propertiesService = inject(PropertiesService);
    private uploadService = inject(PropertyUploadService);
    currencyService = inject(CurrencyService);
    private notifications = inject(NotificationService);
    private confirm = inject(ConfirmDialogService);
    private viewerDialog = inject(ExternalViewerDialogService);
    private store = inject(Store);
    private readonly destroy$ = new Subject<void>();
    private destroyed = false;
    private editPayloadRequestSeq = 0;
    private readonly draftTrigger$ = new Subject<void>();
    private draftStorageKey: string | null = null;
    private draftRestoreCheckedId: string | null = null;
    private suppressDraftWrites = false;
    private readonly mediaAssignedListener = (event: Event): void => {
        const detail = (event as CustomEvent<any>)?.detail;
        if (!detail || String(detail.target_type || '').trim().toLowerCase() !== 'property') return;
        if (String(detail.target_id || '').trim() !== String(this.propertyId || '').trim()) return;
        if (!this.propertyId) return;
        this.loadEditPayload(this.propertyId);
    };
    readonly view = this;
    permissionCodes: string[] = [];
    isAdminContext = false;
    backLink: any[] = ['/developer/properties'];
    backLabel = 'Back to Properties';
    get viewLink(): any[] {
        if (!this.propertyId) return ['/developer/properties'];
        return this.isAdminContext ? ['/admin/properties/view', this.propertyId] : ['/developer/properties/view', this.propertyId];
    }

    get canDuplicateProperty(): boolean {
        return hasPropertyCreatePermission(permissionSetFromRaw(this.permissionCodes));
    }

    paymentPlanManagerHref(): string {
        const params = new URLSearchParams();
        if (this.propertyId) {
            params.set('scope_type', 'property');
            params.set('property_id', this.propertyId);
        } else {
            const projectId = String(this.form.value.projectId || '').trim();
            if (projectId) {
                params.set('scope_type', 'project');
                params.set('project_id', projectId);
            } else {
                params.set('scope_type', 'org_all_properties');
            }
        }
        return buildWorkspaceRouteUrl('developer', 'payment-plans', params);
    }

    form = this.fb.group({
        title: ['', Validators.required],
        tagLine: [''],
        description: [''],
        status: ['', Validators.required],
        price: [''],
        currencyId: ['', Validators.required],
        projectId: ['', Validators.required],
        phaseId: ['', Validators.required],
        country: ['', Validators.required],
        buaEnabled: [true],
        bua: ['', Validators.required],
        buaUnit: ['m²'],
        totalAreaEnabled: [false],
        totalArea: [''],
        totalAreaUnit: ['m²'],
        gardenAreaEnabled: [false],
        gardenArea: [''],
        gardenAreaUnit: ['m²'],
        balconyAreaEnabled: [false],
        balconyArea: [''],
        balconyAreaUnit: ['m²'],
        terraceAreaEnabled: [false],
        terraceArea: [''],
        terraceAreaUnit: ['m²'],
        bedrooms: [''],
        bathrooms: [''],
        numberOfFloors: [''],
        floor: [''],
        unitNumber: [''],
        readyToMove: [''],
        finishingType: [''],
        furnishType: [''],
        cardBadge1: [''],
        cardBadge2: [''],
        discountRateChoice: [''],
        customDiscountRate: [''],
        discountRate: [''],
        maintenanceFeeMode: ['fixed'],
        maintenanceFees: [''],
	        deliveryDate: [''],
	        tags: [''],
	        categoryId: [''],
	        locationAddress: [''],
	        locationLink: [''],
	        useProjectLocation: [true]
	    });

    get useProjectLocation(): boolean {
        return Boolean(this.form.get('useProjectLocation')?.value);
    }

    setUseProjectLocation(value: boolean): void {
        this.form.patchValue({ useProjectLocation: value }, { emitEvent: false });
        this.syncLocationControls(value, { prefillCustom: true });
        this.persistDraft();
    }

    get selectedProject(): Project | null {
        const projectId = String(this.form.value.projectId || '').trim();
        if (!projectId) return null;
        return this.projects.find((p) => String(p?.id || '') === projectId) || null;
    }

    get projectLocationAddress(): string | null {
        const address = this.selectedProject?.locationAddress ?? null;
        return address !== null && address !== undefined && String(address).trim() ? String(address).trim() : null;
    }

    get projectLocationLink(): string | null {
        const link = this.selectedProject?.locationLink ?? null;
        return link !== null && link !== undefined && String(link).trim() ? String(link).trim() : null;
    }

    private syncLocationControls(useProjectLocation: boolean, opts: { prefillCustom?: boolean } = {}): void {
        const address = this.form.get('locationAddress');
        const link = this.form.get('locationLink');
        if (!address || !link) return;

        if (useProjectLocation) {
            address.disable({ emitEvent: false });
            link.disable({ emitEvent: false });
            address.patchValue('', { emitEvent: false });
            link.patchValue('', { emitEvent: false });
            return;
        }

        address.enable({ emitEvent: false });
        link.enable({ emitEvent: false });

        if (opts.prefillCustom) {
            const currentAddress = String(address.value || '').trim();
            const currentLink = String(link.value || '').trim();
            if (!currentAddress && this.projectLocationAddress) {
                address.patchValue(this.projectLocationAddress, { emitEvent: false });
            }
            if (!currentLink && this.projectLocationLink) {
                link.patchValue(this.projectLocationLink, { emitEvent: false });
            }
        }
    }

    propertyId: string | null = null;
    property: Property | null = null;
    currencies: LookupRecord[] = [];
    projects: Project[] = [];
    allPhases: Phase[] = [];
    phases: Phase[] = [];
    images: PropertyImage[] = [];
    loading = true;
    saving = false;
    duplicating = false;
    loadingProjects = false;
    loadingPhases = false;
		    uploadingImage = false;
		    error: string | null = null;
		    uploadError: string | null = null;
	        floorPlanError: string | null = null;
	        uploadingFloorPlan = false;
	        layoutImageError: string | null = null;
	        uploadingLayoutImage = false;
	    nextUploadIsMain = false;
    categories: LookupRecord[] = [];
    propertyTypeSelectOptions: PropertyTypeSelectOption[] = this.buildPropertyTypeSelectOptions([]);
    availableFeatures: LookupRecord[] = [];
    selectedFeatures = new Map<string, string>();
    landmarksForm: FormGroup = this.fb.group({
        items: this.fb.array([this.createLandmarkGroup()])
    });
    paymentPlansForm: FormGroup = this.fb.group({
        plans: this.fb.array([this.createPaymentPlanGroup()])
    });
    private propertyPhaseId: string | null = null;
    showImageModal = false;
    modalImageUrl: string | null = null;
    modalImageIndex = 0;
    steps = [
        { id: 1, label: 'Basics' },
        { id: 2, label: 'Areas' },
        { id: 3, label: 'Layout' },
        { id: 4, label: 'Pricing' },
        { id: 5, label: 'Fees' }
    ];
    currentStep = 1;
    detailTabs: Array<{ id: DetailTabId; label: string }> = [
        { id: 'features', label: 'Features' },
        { id: 'landmarks', label: 'Landmarks' },
        { id: 'plans', label: 'Payment Plans' },
        { id: 'images', label: 'Images' }
    ];
    activeDetailTab: DetailTabId = 'features';
    get activeDetailStep(): number {
        const idx = this.detailTabs.findIndex((t) => t.id === this.activeDetailTab);
        return idx >= 0 ? idx + 1 : 1;
    }
    private dragImageIndex: number | null = null;
    private originalPayloadSnapshot: Record<string, unknown> = {};
    private originalFeaturesSignature = '';
    private originalLandmarksSignature = '';
    private originalPaymentPlansSignature = '';
    private originalImagesSignature = '';
    private originalPublicPriceSource = 'static';
    publicPriceSource = 'static';
    areaUnits = ['m²', 'ft²'];
    distanceUnits = [
        { value: 'm', label: 'm' },
        { value: 'km', label: 'km' },
        { value: 'mile', label: 'mile' }
    ];
    bedroomOptions: Array<number | string> = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '10+'];
    unitFloorOptions: Array<string | number> = ['Ground', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, '20+', 'Penthouse', 'Basement'];
    numberOfFloorsOptions: Array<string | number> = [
        'Ground',
        'Ground + 1',
        'Ground + 2',
        'Ground + 3',
        'Ground + 4',
        'Ground + 5',
        'Ground + 6',
        'Ground + 7',
        'Ground + 8',
        'Ground + 9',
        'Ground + 10',
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9,
        10,
        12,
        15,
        20,
        '20+'
    ];
    furnishOptions = ['Unfurnished', 'Semi-furnished', 'Fully furnished', 'Furnished on request'];
    readonly unitBadgeSecondaryOptions = ['Garden', 'Roof', 'Terrace'];
    finishingOptions = ['Core & Shell', 'Semi-Finished', 'Fully Finished', 'Luxury Finished'];
    discountOptions: Array<number | 'custom'> = [0, 5, 10, 15, 20, 25, 30, 'custom'];
    maintenanceModes = [
        { value: 'fixed', label: 'Fixed amount' },
        { value: 'percent', label: 'Percent of price' }
    ];
    landmarkPresets = LANDMARK_PRESETS;
    planMode: 'manual' | 'auto' | 'none' = 'manual';
	    planGeneratorForm = this.fb.group({
	        scenarios: this.fb.array([this.createPlanScenarioGroup()])
	    });
    private initializingForm = true;
    private currencyChangedByUser = false;
    private updatingCurrencyFromCountry = false;
    private lastCurrencyId: string | null = null;

    get landmarks(): FormArray {
        return this.landmarksForm.get('items') as FormArray;
    }

	    get paymentPlans(): FormArray {
	        return this.paymentPlansForm.get('plans') as FormArray;
	    }

    get effectivePaymentPlans(): Property['paymentPlans'] {
        return (this.property?.paymentPlans || []).filter((plan) => this.paymentPlanHasDetails(plan));
    }

	    get planScenarios(): FormArray {
	        return this.planGeneratorForm.get('scenarios') as FormArray;
	    }

    get floorSelectOptions(): Array<string | number> {
        return this.selectOptionsWithCurrent(this.form.value.floor, this.unitFloorOptions);
    }

    get numberOfFloorsSelectOptions(): Array<string | number> {
        return this.selectOptionsWithCurrent(this.form.value.numberOfFloors, this.numberOfFloorsOptions);
    }

    private paymentPlanHasDetails(plan: any): boolean {
        if (!plan || typeof plan !== 'object') return false;
        return [
            plan?.totalPeriod,
            plan?.paymentCycle,
            plan?.downPayment,
            plan?.downPaymentPercent,
            plan?.reservationAmount,
            plan?.reservationPercent,
            plan?.installmentAmount,
            plan?.numberOfInstallments
        ].some((value) => value !== null && value !== undefined && String(value).trim() !== '');
    }

    get unitBadgePrimaryOptions(): string[] {
        const current = this.unitBadgePrimaryValue;
        return current && !this.furnishOptions.includes(current) ? [current, ...this.furnishOptions] : this.furnishOptions;
    }

    get unitBadgePrimaryValue(): string {
        return this.resolveUnitBadgeState().primary;
    }

    get unitBadgeSecondaryLimit(): number {
        return this.maxUnitSecondaryBadges(this.unitBadgePrimaryValue);
    }

    isUnitSecondaryBadgeSelected(option: string): boolean {
        return this.resolveUnitBadgeState().secondary.includes(option);
    }

    isUnitSecondaryBadgeDisabled(option: string): boolean {
        const state = this.resolveUnitBadgeState();
        return !state.secondary.includes(option) && state.secondary.length >= this.maxUnitSecondaryBadges(state.primary);
    }

    onUnitBadgePrimaryChange(value: string): void {
        const state = this.resolveUnitBadgeState();
        this.applyUnitBadgeSelection(value, state.secondary);
    }

    onUnitSecondaryBadgeToggle(option: string, checked: boolean): void {
        const state = this.resolveUnitBadgeState();
        const normalized = this.normalizeUnitSecondaryBadge(option);
        if (!normalized) return;
        const next = state.secondary.filter((item) => item !== normalized);
        if (checked) next.push(normalized);
        this.applyUnitBadgeSelection(state.primary, next);
    }

    get priceCurrencyLabel(): string {
        return this.normalizeCurrencyCode(this.form.value.currencyId) || this.currencyService.currentCurrency();
    }

    private selectOptionsWithCurrent(value: unknown, options: Array<string | number>): Array<string | number> {
        const v = value === null || value === undefined ? '' : String(value).trim();
        if (!v) return options;
        const exists = options.some(o => String(o).trim() === v);
        return exists ? options : [v, ...options];
    }

    private resolveUnitBadgeState(): { primary: string; secondary: string[] } {
        const badge1 = String(this.form.get('cardBadge1')?.value || '').trim();
        const badge2 = String(this.form.get('cardBadge2')?.value || '').trim();
        const secondary: string[] = [];
        let primary = '';

        const normalizedBadge1 = this.normalizeUnitSecondaryBadge(badge1);
        const normalizedBadge2 = this.normalizeUnitSecondaryBadge(badge2);

        if (normalizedBadge1) secondary.push(normalizedBadge1);
        else if (badge1) primary = badge1;

        if (normalizedBadge2 && !secondary.includes(normalizedBadge2)) secondary.push(normalizedBadge2);
        else if (!primary && badge2) primary = badge2;

        return { primary, secondary };
    }

    private normalizeUnitSecondaryBadge(value: unknown): string | null {
        const normalized = String(value || '').trim().toLowerCase();
        if (!normalized) return null;
        return this.unitBadgeSecondaryOptions.find((option) => option.toLowerCase() === normalized) || null;
    }

    private maxUnitSecondaryBadges(primary: string): number {
        return String(primary || '').trim() ? 1 : 2;
    }

    private applyUnitBadgeSelection(primary: string, secondary: string[]): void {
        const normalizedPrimary = String(primary || '').trim();
        const allowedSecondary = secondary
            .map((option) => this.normalizeUnitSecondaryBadge(option))
            .filter((option): option is string => Boolean(option));
        const uniqueSecondary = [...new Set(allowedSecondary)].slice(0, this.maxUnitSecondaryBadges(normalizedPrimary));

        const badge1 = normalizedPrimary || uniqueSecondary[0] || '';
        const badge2 = normalizedPrimary ? (uniqueSecondary[0] || '') : (uniqueSecondary[1] || '');

        this.form.patchValue({ cardBadge1: badge1, cardBadge2: badge2 }, { emitEvent: false });
        this.touchDraft();
    }

    ngOnInit(): void {
        if (typeof window !== 'undefined') {
            window.addEventListener('nibni:media-library-assigned', this.mediaAssignedListener as EventListener);
        }
        this.draftTrigger$
            .pipe(debounceTime(500), takeUntil(this.destroy$))
            .subscribe(() => this.writeDraft());
        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());
        this.landmarksForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());
        this.paymentPlansForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());

        this.form.get('currencyId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((nextValue) => {
            const nextCurrency = this.normalizeCurrencyCode(nextValue);
            const previousCurrency = this.lastCurrencyId;
            if (!this.initializingForm && !this.updatingCurrencyFromCountry) {
                this.currencyChangedByUser = true;
            }

            if (
                !this.initializingForm
                && !this.updatingCurrencyFromCountry
                && previousCurrency
                && nextCurrency
                && previousCurrency !== nextCurrency
            ) {
                if (!this.convertPriceAndPlans(previousCurrency, nextCurrency)) {
                    this.form.patchValue({ currencyId: previousCurrency }, { emitEvent: false });
                    this.error = 'Exchange rates are unavailable. Currency was kept unchanged to protect the price and payment plans. Try again when rates are available.';
                    this.notifications.showError(this.error).subscribe();
                    return;
                }
            }

            this.lastCurrencyId = nextCurrency;
        });
        this.form.get('country')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((code) => this.applyCountryCurrency(code));
        this.initAreaToggleWatchers();
        this.store.select(selectCurrentUser).pipe(takeUntil(this.destroy$)).subscribe((user: any) => {
            const raw = user?.permission_codes ?? user?.permissionCodes ?? [];
            this.permissionCodes = Array.isArray(raw) ? raw.map((value: any) => String(value || '').trim()) : [];
        });

        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const id = params.get('id');
            if (!id) {
                this.error = 'Missing property id';
                this.loading = false;
                this.initializingForm = false;
                return;
            }

            if (this.propertyId === id) return;

            this.propertyId = id;
            this.draftStorageKey = this.buildDraftStorageKey(id);
            this.draftRestoreCheckedId = null;
            this.currentStep = 1;
	            this.activeDetailTab = 'features';
	            this.error = null;
	            this.uploadError = null;
	            this.property = null;
            this.images = [];
            this.selectedFeatures = new Map<string, string>();
            this.landmarksForm.setControl('items', this.fb.array([this.createLandmarkGroup()]));
            this.paymentPlansForm.setControl('plans', this.fb.array([this.createPaymentPlanGroup()]));

            this.isAdminContext = (this.router.url || '').startsWith('/admin');
            this.backLink = this.isAdminContext ? ['/admin/properties/view', this.propertyId] : ['/developer/properties'];
            this.backLabel = this.isAdminContext ? 'Back to Property' : 'Back to Properties';

            this.loadEditPayload(this.propertyId);
        });
    }

    private loadEditPayload(id: string) {
        const requestSeq = ++this.editPayloadRequestSeq;
        this.loading = true;
        this.loadingProjects = true;
        this.loadingPhases = true;
        this.propertiesService.getPropertyEditPayload(id).subscribe({
            next: (payload) => {
                if (this.destroyed || requestSeq !== this.editPayloadRequestSeq || this.propertyId !== id) return;
                this.projects = (payload.projects || []).map((proj: any) => ({
                    id: String(proj?.ROWID || proj?.row_id || proj?.id || ''),
                    name: proj?.NAME || proj?.Title || proj?.TITLE || proj?.name || 'Untitled',
                    locationAddress: proj?.LOCATION_ADDRESS ?? proj?.location_address ?? proj?.locationAddress ?? null,
                    locationLink: proj?.LOCATION_LINK ?? proj?.location_link ?? proj?.locationLink ?? null,
                    images: []
                }));
                this.categories = payload.categories || [];
                this.propertyTypeSelectOptions = this.buildPropertyTypeSelectOptions(this.categories);
                this.availableFeatures = payload.features || [];
                this.currencies = payload.currencies || [];
                this.allPhases = (payload.phases || []).map((phase: any) => ({
                    id: String(phase?.ROWID || phase?.row_id || phase?.id || ''),
                    name: phase?.NAME || phase?.name || 'Unnamed phase',
                    status: phase?.STATUS || phase?.status || null,
                    project_id: String(phase?.PROJECT_ID || phase?.project_id || '')
                }));
                this.property = payload.property;
                if (payload.property) {
                    const currentCategoryId = (payload.property as any)?.categoryId ? String((payload.property as any).categoryId) : '';
                    const currentCategoryLabel = (payload.property as any)?.categoryName || (payload.property as any)?.type || null;
                    if (currentCategoryId && !this.propertyTypeSelectOptions.some((opt) => opt.value === currentCategoryId)) {
                        const label = currentCategoryLabel ? String(currentCategoryLabel).trim() : 'Current type';
                        const placeholder = this.propertyTypeSelectOptions[0];
                        const rest = this.propertyTypeSelectOptions.slice(1);
                        this.propertyTypeSelectOptions = [
                            placeholder,
                            { label: label || 'Current type', value: currentCategoryId },
                            ...rest
                        ];
                    }
                    this.setImages(payload.property.images || []);
                    this.patchForm(payload.property);
                    this.propertyPhaseId = payload.property.phaseId || payload.property.phase?.id || null;
                    this.setCurrencyFromProperty();
                    // Only auto-derive currency if backend did not provide one.
                    if (!this.form.value.currencyId) {
                        this.applyCountryCurrency(this.form.value.country);
                    }

                    void this.maybeRestoreDraft(id);
                }
                const projectId = this.form.value.projectId || (payload.property as any)?.project_id || null;
                this.phases = projectId
                    ? (this.allPhases || []).filter((p) => String(p?.project_id || '') === String(projectId))
                    : [];
                this.loading = false;
                this.loadingProjects = false;
                this.loadingPhases = false;
                this.initializingForm = false;
            },
            error: (err: any) => {
                if (this.destroyed || requestSeq !== this.editPayloadRequestSeq || this.propertyId !== id) return;
                this.error = err?.error?.error?.message || err?.error?.message || err?.message || 'Unable to load property.';
                this.propertyTypeSelectOptions = this.buildPropertyTypeSelectOptions([]);
                this.loading = false;
                this.loadingProjects = false;
                this.loadingPhases = false;
                this.initializingForm = false;
            }
        });
    }

    private patchForm(property: Property) {
        const countryValue =
            (property as any)?.countryCode ||
            (property as any)?.country?.CODE ||
            (property as any)?.country?.code ||
            property.country ||
            '';
        const normalizedCountry = this.normalizeCountryCode(countryValue);
        const hasBua = property.bua !== null && property.bua !== undefined;
        const hasTotalArea = property.totalArea !== null && property.totalArea !== undefined;
        const hasGardenArea = property.gardenArea !== null && property.gardenArea !== undefined;
        const hasBalconyArea = property.balconyArea !== null && property.balconyArea !== undefined;
        const hasTerraceArea = property.terraceArea !== null && property.terraceArea !== undefined;
        const discountChoice = this.resolveDiscountChoice(property.discountRate);
        const normalizedStatus = ['reserved', 'hold'].includes((property.status || '').toLowerCase()) ? 'ReservedOrHold' : property.status || '';
        const hasCustomLocation = Boolean(String(property.locationAddress || '').trim() || String(property.locationLink || '').trim());
        const useProjectLocation = !hasCustomLocation;
        this.form.patchValue({
            title: property.title,
            tagLine: property.tagLine || '',
            description: property.description || '',
            status: normalizedStatus,
            price: property.price != null ? property.price.toString() : '',
            currencyId: property.currencyId || '',
            projectId: property.project_id || '',
            phaseId: property.phaseId || property.phase?.id || '',
            country: normalizedCountry,
            buaEnabled: hasBua,
            bua: property.bua != null ? property.bua.toString() : '',
            buaUnit: (property as any)?.buaUnit || 'm²',
            totalAreaEnabled: hasTotalArea,
            totalArea: property.totalArea != null ? property.totalArea.toString() : '',
            totalAreaUnit: (property as any)?.totalAreaUnit || 'm²',
            gardenAreaEnabled: hasGardenArea,
            gardenArea: property.gardenArea != null ? property.gardenArea.toString() : '',
            gardenAreaUnit: (property as any)?.gardenAreaUnit || 'm²',
            balconyAreaEnabled: hasBalconyArea,
            balconyArea: property.balconyArea != null ? property.balconyArea.toString() : '',
            balconyAreaUnit: (property as any)?.balconyAreaUnit || 'm²',
            terraceAreaEnabled: hasTerraceArea,
            terraceArea: property.terraceArea != null ? property.terraceArea.toString() : '',
            terraceAreaUnit: (property as any)?.terraceAreaUnit || 'm²',
            bedrooms: property.bedrooms != null ? property.bedrooms.toString() : '',
            bathrooms: property.bathrooms != null ? property.bathrooms.toString() : '',
            numberOfFloors: property.numberOfFloors ?? '',
            floor: property.floor ?? '',
            unitNumber: property.unitNumber ?? '',
            readyToMove: property.readyToMove === true ? 'true' : property.readyToMove === false ? 'false' : '',
            finishingType: property.finishingType ?? '',
            furnishType: property.furnishType ?? '',
            cardBadge1: property.cardBadge1 ?? '',
            cardBadge2: property.cardBadge2 ?? '',
            discountRateChoice: discountChoice === 'custom' ? 'custom' : discountChoice === '' ? '' : discountChoice?.toString?.() || '',
            customDiscountRate: discountChoice === 'custom' && property.discountRate != null ? property.discountRate.toString() : '',
            discountRate: property.discountRate != null ? property.discountRate.toString() : '',
            maintenanceFeeMode: (property as any)?.maintenanceFeeMode || 'fixed',
            maintenanceFees: property.maintenanceFees != null ? property.maintenanceFees.toString() : '',
            deliveryDate: this.normalizeDateForInput(property.deliveryDate) || '',
            tags: property.tags ? property.tags.join(', ') : '',
            categoryId: (property as any).categoryId || '',
            locationAddress: property.locationAddress || '',
            locationLink: property.locationLink || '',
            useProjectLocation
        });
        // If normalization failed but backend sent something, preserve it to display in the selector.
        if (!this.form.value.country && countryValue) {
            this.form.patchValue({ country: countryValue.toString().trim() });
        }
        this.propertyPhaseId = property.phaseId || property.phase?.id || null;
        this.setCurrencyFromProperty();
        this.lastCurrencyId = this.normalizeCurrencyCode(this.form.value.currencyId);
        this.populateFeatures(property.featureValues || property.features || []);
        this.populateLandmarks(property.landmarks || []);
        this.populatePaymentPlans(property.paymentPlans || []);
        this.publicPriceSource = this.sourceValueFromPaymentPlanId(property.offerDefaultPaymentPlanId || null);
        this.originalPublicPriceSource = this.publicPriceSource;
        this.originalPayloadSnapshot = this.normalizePayloadFromPropertyData(property);
        this.captureSnapshots(property);
        this.syncLocationControls(useProjectLocation);
    }

    private setCurrencyFromProperty() {
        if (!this.property || !this.currencies.length) return;
        const match = this.property.currencyId
            ? this.currencies.find(c => c.ROWID === this.property?.currencyId)
            : this.currencies.find(c => (c.CODE || '').toLowerCase() === (this.property?.currency || '').toLowerCase());
        if (match) {
            this.updatingCurrencyFromCountry = true;
            this.form.patchValue({ currencyId: match.ROWID }, { emitEvent: false });
            this.updatingCurrencyFromCountry = false;
            this.lastCurrencyId = this.normalizeCurrencyCode(match.ROWID);
        }
    }

    fieldInvalid(controlName: string): boolean {
        const control = this.form.get(controlName);
        return !!(control && control.invalid && (control.dirty || control.touched));
    }

    inputClasses(controlName: string): string {
        return [
            'w-full rounded-lg px-3 py-2 text-white placeholder:text-white/50 focus:outline-none bg-white/10',
            this.fieldInvalid(controlName) ? 'border border-red-500 focus:border-red-400' : 'border border-white/20 focus:border-[#AA9072]'
        ].join(' ');
    }

    private buildPropertyTypeSelectOptions(categories: LookupRecord[]): PropertyTypeSelectOption[] {
        const options = (categories || [])
            .map((cat) => {
                const label = (cat?.NAME ?? '').toString().trim();
                const value = (cat?.ROWID ?? '').toString().trim();
                if (!label || !value) return null;
                return { label, value };
            })
            .filter(Boolean) as Array<{ label: string; value: string }>;

        return [{ label: 'Select property type', value: '', disabled: true }, ...options];
    }

    private mediaWorkspace(): MediaLibraryWorkspace {
        return this.isAdminContext ? 'admin' : 'developer';
    }

    openPropertyMediaPicker(slotKey: 'floor_plan' | 'layout_image' | 'gallery', tab: 'view' | 'upload' = 'view'): void {
        if (!this.propertyId) return;
        openMediaLibraryPopup(this.mediaWorkspace(), {
            tab,
            targetType: 'property',
            slotKey,
            targetId: this.propertyId,
            targetLabel: String(this.form.value.title || this.property?.title || this.property?.unitNumber || 'Current unit').trim() || 'Current unit',
            visibility: 'public'
        });
    }

    async save() {
        if (!this.propertyId) return;
        this.error = null;
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.error = 'Please fill in the required fields highlighted in red.';
            this.notifications.showError(this.error).subscribe();
            return;
        }
        const value = this.form.value;
        const buaEnabled = value.buaEnabled !== false;
        const buaValue = this.parseNumber(value.bua);
        if (buaEnabled) {
            if (buaValue === null || buaValue === undefined || !Number.isFinite(buaValue) || buaValue <= 0) {
                this.error = 'Built-up Area must be a positive number when enabled.';
                this.notifications.showError(this.error).subscribe();
                return;
            }
        }
        const planValidationError = this.validatePaymentPlans();
        if (planValidationError) {
            this.error = planValidationError;
            this.notifications.showError(planValidationError).subscribe();
            return;
        }

        const locationLink = value.locationLink ? String(value.locationLink).trim() : '';
        if (locationLink && !/^https?:\/\//i.test(locationLink)) {
            this.error = 'Location link must start with http(s)://';
            this.notifications.showError(this.error).subscribe();
            return;
        }

        const normalized = this.normalizePayloadFromFormValue(value);
        const changedPayload: Record<string, unknown> = {};
        Object.entries(normalized).forEach(([key, val]) => {
            const originalVal = this.originalPayloadSnapshot[key];
            if (val !== originalVal) {
                changedPayload[key] = val;
            }
        });

        const featuresChanged = this.serializeSelectedFeatures() !== this.originalFeaturesSignature;
        const landmarksChanged = this.serializeLandmarks(this.landmarks.value as any) !== this.originalLandmarksSignature;
        const currencyChanged = normalized['currency_code'] !== this.originalPayloadSnapshot['currency_code'];
        const paymentPlansChanged = currencyChanged || this.serializePaymentPlans(this.paymentPlans.value as any) !== this.originalPaymentPlansSignature;
        const imagesChanged = this.serializeImages(this.images || []) !== this.originalImagesSignature;
        const publicPriceSourceChanged = this.publicPriceSource !== this.originalPublicPriceSource;
        const requiresPropertySave = Boolean(Object.keys(changedPayload).length || featuresChanged || landmarksChanged || paymentPlansChanged || imagesChanged);
        const hasAnyChange = requiresPropertySave || publicPriceSourceChanged;

        if (!hasAnyChange) {
            this.error = 'No changes to save. Only modified fields are sent.';
            this.notifications.showError(this.error).subscribe();
            return;
        }

        this.saving = true;
        try {
            const savePayload: Record<string, unknown> = { ...changedPayload };
            const targetPublicPriceSource = this.publicPriceSource;

            if (featuresChanged) {
                savePayload['features'] = Array.from(this.selectedFeatures.entries()).map(([featureId, value]) => ({
                    feature_id: featureId,
                    value: value || 'Yes'
                }));
            }

                if (landmarksChanged) {
                    const landmarks = (this.landmarks.value as Array<any>).map((lm) => {
                        const presetName = lm?.type && lm.type !== 'custom' ? String(lm.type).trim() : '';
                        const name = (lm?.name || presetName || '').toString().trim();
                        const distanceValue = this.parseNumber(lm?.distance);
                        const distanceUnit = this.normalizeDistanceUom(lm?.distanceUnit);
                        const distanceMeter = this.distanceToMeters(distanceValue, distanceUnit);
                        const timeRaw = this.parseInt(lm?.timeMinutes);
                        const timeMinutes = timeRaw !== null && timeRaw > 0 ? timeRaw : null;
                        if (!name && distanceMeter === null && timeMinutes === null) return null;
                        return {
                            id: lm?.id ? String(lm.id).trim() : null,
                            name,
                            distance_meter: distanceMeter,
                            distance_uom: distanceUnit,
                            time_minutes: timeMinutes
                        };
                    }).filter(Boolean);
                    savePayload['landmarks'] = landmarks;
                }

	            if (paymentPlansChanged) {
	                savePayload['payment_plans_currency_code'] = normalized['currency_code'];
	                savePayload['plan_mode'] = this.planMode;
	                const basePrice = this.getBasePrice();
	                const plans = this.planMode === 'none'
	                    ? []
	                    : (this.paymentPlans.value as Array<any>).map((plan) => {
	                        const downPercent = this.parseNumber(plan?.downPayment);
	                        const reservePercent = this.parseNumber(plan?.reservationAmount);
	                        // Use the plan's OWN displayPrice as base for percent→amount conversion,
	                        // falling back to the property price only if displayPrice is not set.
	                        // This prevents corruption when plans have different display prices.
	                        const planBasePrice = this.parseNumber(plan?.displayPrice) ?? basePrice;
	                        const resolveFromPercent = (pct: number | null): number | null => {
	                            if (!planBasePrice || pct === null || pct === undefined) return null;
	                            return (planBasePrice * pct) / 100;
	                        };
                        const downAmount = resolveFromPercent(downPercent) ?? this.parseNumber(plan?.downPayment);
                        const reservationAmount = resolveFromPercent(reservePercent) ?? this.parseNumber(plan?.reservationAmount);
	                        // Auto-recompute installment to ensure components sum to display_price
	                        const nInst = this.parseInt(plan?.numberOfInstallments);
	                        const displayPrice = this.parseNumber(plan?.displayPrice);
	                        let installmentAmount = this.parseNumber(plan?.installmentAmount);
	                        if (displayPrice !== null && nInst !== null && nInst > 0) {
	                            const remaining = displayPrice - (reservationAmount ?? 0) - (downAmount ?? 0);
	                            if (remaining > 0) {
	                                installmentAmount = Math.round((remaining / nInst) * 100) / 100;
	                            }
	                        }
	                        const hasValue = plan?.paymentCycle || installmentAmount || nInst || plan?.totalPeriod || downPercent !== null || reservePercent !== null || displayPrice !== null;
	                        if (!hasValue) return null;
	                        const totalPeriod = this.parseInt(plan?.totalPeriod);
	                        if (!totalPeriod || totalPeriod <= 0) return null;
	                        const paymentTermsFromRows = this.buildPaymentTermsFromExtraRows(plan, basePrice);
	                        const managed = plan?.paymentTermsManaged === true || String(plan?.paymentTermsManaged || '').toLowerCase() === 'true';
	                        const extraRows = Array.isArray(plan?.extraRows) ? plan.extraRows : [];
	                        const shouldClearTerms = managed && extraRows.length === 0;
	                        const rawTerms = typeof plan?.paymentTerms === 'string' ? plan.paymentTerms.trim() : '';
	                        const paymentTerms = paymentTermsFromRows !== null ? paymentTermsFromRows : shouldClearTerms ? null : (rawTerms || null);
	                        return {
	                            id: plan?.id ? String(plan.id).trim() : null,
	                            total_period: totalPeriod,
	                            payment_cycle: this.parseInt(plan?.paymentCycle),
	                            downpayment: downAmount ?? 0,
		                            reservation_amount: reservationAmount ?? 0,
		                            installment_amount: installmentAmount,
		                            number_of_installments: nInst,
		                            display_price: displayPrice,
		                            payment_terms: paymentTerms
	                        };
	                    }).filter(Boolean);
	                savePayload['payment_plans'] = plans;
	            }

            if (imagesChanged) {
                this.images = this.assignImageOrder(this.images);
                const images = (this.images || []).map((img, idx) => {
                    if (!img?.id) return null;
                    const label = this.deriveImageLabel(img.alt || img.url, idx);
                    return {
                        id: String(img.id),
                        alt: label,
                        sort_order: idx + 1,
                        is_main: Boolean(img.isMain)
                    };
                }).filter(Boolean);
                savePayload['images'] = images;
            }

            const currentPlans = (this.paymentPlans.getRawValue?.() as Array<any>) || [];
            let savedProperty = this.property;

            if (requiresPropertySave) {
                const response = await firstValueFrom(
                    this.propertiesService.savePropertyEditPayload(this.propertyId, savePayload).pipe(takeUntil(this.destroy$))
                );

                const saved = response?.property;
                if (saved) {
                    this.suppressDraftWrites = true;
                    try {
                        this.property = saved;
                        savedProperty = saved;
                        this.setImages((saved.images || []) as any);
                        this.patchForm(saved);
                    } finally {
                        this.suppressDraftWrites = false;
                    }
                }
            }

            if (publicPriceSourceChanged) {
                const persistedPlanId = this.resolveSelectedPublicPricePlanId(targetPublicPriceSource, currentPlans, savedProperty?.paymentPlans || []);
                await firstValueFrom(
                    this.propertiesService.updateOfferDefaults(this.propertyId, {
                        offer_default_payment_plan_id: persistedPlanId
                    }).pipe(takeUntil(this.destroy$))
                );
            }

            this.originalPayloadSnapshot = this.normalizePayloadFromPropertyData(this.property as any);
            this.captureSnapshots(this.property as any);
            this.notifications.showSuccess('Property updated').subscribe();
            this.clearDraft();
            this.loadEditPayload(this.propertyId);
        } catch (err) {
            this.error = this.getErrorMessage(err, 'Failed to update property');
            this.notifications.showError(this.error || 'Failed to update property').subscribe();
        } finally {
            this.saving = false;
        }
    }

    async duplicateProperty() {
        if (!this.canDuplicateProperty) return;
        if (!this.propertyId || this.saving || this.duplicating) return;

        const confirmed = await this.confirm.confirm({
            title: 'Duplicate property?',
            message: 'This will duplicate the last saved version, including images, payment plans, features, and landmarks.',
            confirmText: 'Duplicate',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;

        this.error = null;
        this.duplicating = true;
        try {
            const duplicated = await firstValueFrom(
                this.propertiesService.duplicateProperty(this.propertyId).pipe(takeUntil(this.destroy$))
            );
            const newId = String((duplicated as any)?.id || '').trim();
            if (!newId) throw new Error('Duplicate succeeded but new property id was not returned.');

            this.notifications.showSuccess('Property duplicated').subscribe();

            const base = this.isAdminContext ? '/admin' : '/developer';
            await this.router.navigate([base, 'properties', 'edit', newId]);
        } catch (err) {
            this.error = this.getErrorMessage(err, 'Failed to duplicate property');
            this.notifications.showError(this.error || 'Failed to duplicate property').subscribe();
        } finally {
            this.duplicating = false;
        }
    }

    get canStripCopyTitle(): boolean {
        const raw = String(this.form?.get('title')?.value || '').trim();
        return /\(copy\)\s*$/i.test(raw);
    }

    stripCopySuffix(): void {
        const ctrl = this.form?.get('title');
        if (!ctrl) return;
        const raw = String(ctrl.value || '');
        const next = raw.replace(/\s*\(copy\)\s*$/i, '').trim();
        if (!next || next === raw) return;
        ctrl.setValue(next);
        ctrl.markAsDirty();
        this.notifications.showSuccess('Removed (Copy). Save to apply.').subscribe();
    }

    handleImageUpload(event: Event) {
        if (!this.propertyId) return;
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        if (!files.length) return;
        this.uploadError = null;
        this.uploadingImage = true;

        const uploads = files.map((file, idx) => {
            const markAsMain = this.nextUploadIsMain && idx === 0;
            const nextOrder = this.images.length + idx + 1;
            const alt = file.name ? this.deriveImageLabel(file.name, this.images.length + idx) : null;
            const upload$ = this.uploadService.uploadFile(file, { isPublic: true, propertyId: this.propertyId }).pipe(
                switchMap(({ file_id }) =>
                    this.uploadService.attachPropertyImage(this.propertyId!, {
                        file_id,
                        alt,
                        is_main: markAsMain,
                        sort_order: nextOrder
                    })
                )
            );
            return { upload$, markAsMain, alt };
        });

        let finished = 0;
        const finalizeUpload = () => {
            finished += 1;
            if (finished >= uploads.length) {
                this.uploadingImage = false;
                this.nextUploadIsMain = false;
            }
        };

        uploads.forEach(({ upload$, markAsMain, alt }) => {
            upload$.pipe(takeUntil(this.destroy$), finalize(finalizeUpload)).subscribe({
                next: (resp) => {
                    const data = resp?.data || resp || {};
                    const url = data?.URL || data?.url || data?.fileUrl || data?.file_url || null;
                    if (url) {
                        const thumb = data?.URL_THUMB || data?.thumbUrl || data?.thumb_url || data?.thumb || data?.thumbnail || null;
                        const id = data?.ROWID || data?.row_id || data?.id || data?.imageId || null;
                        const isMain = Boolean(markAsMain || data?.isMain || data?.IS_MAIN);
                        const newImage: PropertyImage = {
                            id,
                            url,
                            thumb,
                            order: Number(data?.IMAGE_ORDER ?? this.images.length + 1) || this.images.length + 1,
                            isMain,
                            alt: data?.ALT || alt || undefined
                        };
                        if (newImage.isMain) {
                            this.images = this.images.map(img => ({ ...img, isMain: false }));
                        }
                        this.setImages([...this.images, newImage]);
                    }
                },
                error: (err) => {
                    this.uploadError = this.getErrorMessage(err, 'Failed to upload image');
                    this.notifications.showError(this.uploadError).subscribe();
                }
            });
        });

        input.value = '';
    }

    onImageDragStart(index: number, event?: DragEvent) {
        this.dragImageIndex = index;
        if (event?.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(index));
        }
    }

    onImageDragOver(event: DragEvent, targetIndex?: number) {
        event.preventDefault();
        if (this.dragImageIndex === null) return;
        if (typeof targetIndex === 'number' && targetIndex !== this.dragImageIndex) {
            const updated = [...this.images];
            const [moved] = updated.splice(this.dragImageIndex, 1);
            updated.splice(targetIndex, 0, moved);
            this.dragImageIndex = targetIndex;
            this.setImages(updated, true);
        }
    }

    onImageDrop(targetIndex: number, event?: DragEvent) {
        event?.preventDefault();
        if (this.dragImageIndex === null || this.dragImageIndex === targetIndex) return;
        const updated = [...this.images];
        const [moved] = updated.splice(this.dragImageIndex, 1);
        updated.splice(targetIndex, 0, moved);
        this.setImages(updated.map(img => ({ ...img })), true);
        this.dragImageIndex = null;
    }

    onImageDragEnd() {
        this.dragImageIndex = null;
    }

    moveImage(fromIndex: number, delta: number) {
        const toIndex = fromIndex + delta;
        if (toIndex < 0 || toIndex >= this.images.length) return;
        const updated = [...this.images];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved);
        this.setImages(updated, true);
    }

    deleteImage(imageUrl: string, index: number) {
        if (!this.propertyId || !imageUrl) return;
        this.uploadError = null;
        const imageId = this.images[index]?.id || null;
        if (!imageId) {
            this.uploadError = 'Cannot delete image: missing image id.';
            this.notifications.showError(this.uploadError).subscribe();
            return;
        }
        this.uploadService.deletePropertyImage(this.propertyId, String(imageId)).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.images.splice(index, 1);
                this.setImages([...this.images]);
                this.notifications.showSuccess('Image deleted').subscribe();
            },
            error: (err) => {
                this.uploadError = this.getErrorMessage(err, 'Failed to delete image');
                this.notifications.showError(this.uploadError).subscribe();
            }
        });
    }

    openImageModal(target: number | string) {
        const index = typeof target === 'number' ? target : this.images.findIndex(img => img.url === target);
        this.modalImageIndex = index >= 0 ? index : 0;
        this.modalImageUrl = this.images[this.modalImageIndex]?.url || null;
        this.showImageModal = !!this.modalImageUrl;
    }

    cycleModalImage(delta: number) {
        if (!this.images.length) return;
        const total = this.images.length;
        this.modalImageIndex = (this.modalImageIndex + delta + total) % total;
        this.modalImageUrl = this.images[this.modalImageIndex]?.url || null;
    }

    closeImageModal() {
        this.modalImageUrl = null;
        this.showImageModal = false;
        this.modalImageIndex = 0;
    }

    prevStep() {
        if (this.currentStep > 1) this.currentStep--;
    }

    nextStep() {
        if (this.currentStep < this.steps.length) this.currentStep++;
    }

    goToStep(stepId: number) {
        this.currentStep = stepId;
    }

    setActiveDetailTab(tab: DetailTabId) {
        this.activeDetailTab = tab;
    }

    addLandmark() {
        this.landmarks.push(this.createLandmarkGroup());
    }

    removeLandmark(index: number) {
        if (this.landmarks.length === 1) return;
        this.landmarks.removeAt(index);
    }

    applyLandmarkPreset(index: number) {
        const group = this.landmarks.at(index) as FormGroup | undefined;
        const preset = group?.get('type')?.value;
        if (group && preset && preset !== 'custom') {
            group.patchValue({ name: preset });
        }
    }

	    addPaymentPlan() {
	        if (this.planMode === 'none') {
	            this.planMode = 'manual';
	        }
	        this.paymentPlans.push(this.createPaymentPlanGroup());
	    }

	    paymentPlanRows(planIndex: number): FormArray {
	        const plan = this.paymentPlans.at(planIndex) as FormGroup | null;
	        if (!plan) return this.fb.array([]);
	        let rows = plan.get('extraRows') as FormArray | null;
	        if (!rows) {
	            rows = this.fb.array([]);
	            plan.setControl('extraRows', rows);
	        }
	        return rows;
	    }

	    addPaymentPlanRow(planIndex: number) {
	        const rows = this.paymentPlanRows(planIndex);
	        rows.push(this.createPaymentPlanRowGroup());
	    }

	    removePaymentPlanRow(planIndex: number, rowIndex: number) {
	        const rows = this.paymentPlanRows(planIndex);
	        if (rowIndex < 0 || rowIndex >= rows.length) return;
	        rows.removeAt(rowIndex);
	    }

	    paymentPlanRowAmountLabel(planIndex: number, rowIndex: number): string {
	        const amount = this.paymentPlanRowAmount(planIndex, rowIndex);
	        if (amount === null) return '—';
	        const currency = this.priceCurrencyLabel;
	        const formatted = this.formatNumberNoDecimals(amount);
	        return currency ? `${formatted} ${currency}` : formatted;
	    }

	    private paymentPlanRowAmount(planIndex: number, rowIndex: number): number | null {
	        const price = this.getBasePrice();
	        if (price === null) return null;
	        const rows = this.paymentPlanRows(planIndex);
	        const row = rows.at(rowIndex)?.value as any;
	        const percent = this.parseNumber(row?.percent);
	        if (percent === null) return null;
	        return (price * percent) / 100;
	    }

	    removePaymentPlan(index: number) {
	        if (this.paymentPlans.length === 1) return;
	        this.paymentPlans.removeAt(index);
            this.rebasePublicPriceSourceAfterPlanRemoval(index);
            this.touchDraft();
	    }

	    setPlanMode(mode: 'manual' | 'auto' | 'none') {
	        this.planMode = mode;
	        if (mode === 'none') {
	            this.paymentPlans.clear();
                this.publicPriceSource = 'static';
	        } else if (this.paymentPlans.length === 0) {
	            this.paymentPlans.push(this.createPaymentPlanGroup());
	        }
            this.normalizePublicPriceSourceSelection();
            this.touchDraft();
	    }

	    private createPlanScenarioGroup(seed?: any): FormGroup {
	        const safe = seed && typeof seed === 'object' ? seed : {};
	        return this.fb.group({
	            years: [this.parseInt(safe.years) ?? 1],
	            paymentFrequency: [safe.paymentFrequency ?? 'monthly'],
	            downPercent: [this.parseNumber(safe.downPercent) ?? 10],
	            reservationPercent: [this.parseNumber(safe.reservationPercent) ?? 5]
	        });
	    }

	    addPlanScenario(seed?: Partial<{ years: number; paymentFrequency: string; downPercent: number; reservationPercent: number }>) {
	        const last = this.planScenarios.length ? (this.planScenarios.at(this.planScenarios.length - 1)?.value as any) : {};
	        const base = {
	            years: this.parseInt(last?.years) ?? 1,
	            paymentFrequency: last?.paymentFrequency ?? 'monthly',
	            downPercent: this.parseNumber(last?.downPercent) ?? 10,
	            reservationPercent: this.parseNumber(last?.reservationPercent) ?? 5
	        };
	        this.planScenarios.push(this.createPlanScenarioGroup({ ...base, ...(seed || {}) }));
	    }

	    removePlanScenario(index: number) {
	        if (this.planScenarios.length <= 1) return;
	        this.planScenarios.removeAt(index);
	    }

	    resetPlanScenarios(scenarios?: any[]) {
	        this.planScenarios.clear();
	        const source = Array.isArray(scenarios) && scenarios.length ? scenarios : [null];
	        source.forEach((s) => this.planScenarios.push(this.createPlanScenarioGroup(s)));
	    }

	    addPlanScenarioPreset(years: number) {
	        const safeYears = Number.isFinite(years) && years > 0 ? years : 1;
	        this.addPlanScenario({ years: safeYears });
	    }

	    planScenarioPreview(index: number): string {
	        const price = this.getBasePrice();
	        if (!price || price <= 0) return 'Enter a price to preview.';
	        const scenario = this.planScenarios.at(index)?.value as any;
	        const computed = this.computeGeneratedPlan(scenario, price);
	        if (!computed) return '—';
	        const currency = this.priceCurrencyLabel;
	        const freqLabel = computed.paymentCycle === 12 ? 'Yearly' : computed.paymentCycle === 3 ? 'Quarterly' : 'Monthly';
	        const installmentLabel = `${this.formatNumberNoDecimals(computed.installmentAmount)}${currency ? ` ${currency}` : ''}`;
	        return `${computed.totalPeriod / 12}y • ${freqLabel} • ${computed.numberOfInstallments} installments • Installment ${installmentLabel}`;
	    }

	    private computeGeneratedPlan(scenario: any, price: number): {
	        totalPeriod: number;
	        paymentCycle: number;
	        downPct: number;
	        reservePct: number;
	        installmentAmount: number;
	        numberOfInstallments: number;
	    } | null {
	        if (!price || price <= 0) return null;
	        const safe = scenario && typeof scenario === 'object' ? scenario : {};
	        const years = this.parseInt(safe.years) || 1;
	        const frequency = (safe.paymentFrequency || 'monthly').toString();
	        const downPct = Math.max(0, Math.min(100, this.parseNumber(safe.downPercent) ?? 0));
	        const reservePct = Math.max(0, Math.min(100, this.parseNumber(safe.reservationPercent) ?? 0));
	        const paymentCycle = frequency === 'Quarterly' ? 3 : frequency === 'yearly' ? 12 : 1;
	        const totalPeriod = years * 12;
	        const numberOfInstallments = Math.max(1, Math.floor(totalPeriod / paymentCycle));
	        const downAmount = (price * downPct) / 100;
	        const reserveAmount = (price * reservePct) / 100;
	        const remaining = Math.max(0, price - downAmount - reserveAmount);
	        const installmentAmount = numberOfInstallments ? remaining / numberOfInstallments : remaining;
	        return {
	            totalPeriod,
	            paymentCycle,
	            downPct,
	            reservePct,
	            installmentAmount: Number(installmentAmount.toFixed(2)),
	            numberOfInstallments
	        };
	    }

		    generateAutoPlan() {
		        const basePrice = this.getBasePrice();
		        if (!basePrice || basePrice <= 0) {
		            this.notifications.showError('Enter a price to generate a payment plan.').subscribe();
	            return;
	        }
	        const scenarios = (this.planScenarios.getRawValue?.() as any) || [];
	        const computed = (Array.isArray(scenarios) ? scenarios : [])
	            .map((scenario: any) => this.computeGeneratedPlan(scenario, basePrice))
	            .filter(Boolean) as Array<{
	            totalPeriod: number;
	            paymentCycle: number;
	            downPct: number;
	            reservePct: number;
	            installmentAmount: number;
	            numberOfInstallments: number;
	        }>;

	        if (!computed.length) {
	            this.notifications.showError('Add at least one scenario to generate plans.').subscribe();
	            return;
	        }

	        this.paymentPlans.clear();
	        computed.forEach((plan) => {
	            const group = this.createPaymentPlanGroup();
	            group.patchValue(
	                {
	                    totalPeriod: plan.totalPeriod,
	                    paymentCycle: plan.paymentCycle,
		                    downPayment: plan.downPct,
		                    reservationAmount: plan.reservePct,
		                    displayPrice: basePrice,
		                    installmentAmount: plan.installmentAmount,
	                    numberOfInstallments: plan.numberOfInstallments
	                },
	                { emitEvent: false }
	            );
	            this.paymentPlans.push(group);
	        });
	        this.planMode = 'auto';
	    }

    isFeatureSelected(id: string): boolean {
        return this.selectedFeatures.has(id);
    }

    badgeFeatureAvailable(name: string): boolean {
        return Boolean(this.badgeFeatureOption(name));
    }

    isNamedFeatureSelected(name: string): boolean {
        const feature = this.badgeFeatureOption(name);
        return Boolean(feature && this.selectedFeatures.has(feature.ROWID));
    }

    toggleNamedFeature(name: string, event: Event): void {
        const feature = this.badgeFeatureOption(name);
        const target = event.target as HTMLInputElement | null;
        if (!feature || !target) return;
        this.toggleFeature(feature.ROWID, Boolean(target.checked));
    }

    toggleFeature(id: string, checked: boolean) {
        if (checked) {
            if (!this.selectedFeatures.has(id)) this.selectedFeatures.set(id, '');
        } else {
            this.selectedFeatures.delete(id);
        }
        this.touchDraft();
    }

    updateFeatureValue(id: string, value: string) {
        if (!this.selectedFeatures.has(id)) return;
        this.selectedFeatures.set(id, value);
        this.touchDraft();
    }

    private parseNumber(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private badgeFeatureOption(name: string): LookupRecord | null {
        const expected = String(name || '').trim().toLowerCase();
        if (!expected) return null;
        return this.availableFeatures.find((feature) => String(feature?.NAME || '').trim().toLowerCase() === expected) || null;
    }

	    private formatPercent(value: number): string {
	        return value.toFixed(2).replace(/\.?0+$/, '');
	    }

	    private formatNumberNoDecimals(value: any): string {
	        const parsed = this.parseNumber(value);
	        if (parsed === null) return '—';
	        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(parsed);
	    }

    publicPriceSourceOptions(): Array<{ value: string; label: string }> {
        const options: Array<{ value: string; label: string }> = [];
        const staticLabel = this.publicPriceAmountLabel(this.getBasePrice());
        options.push({
            value: 'static',
            label: staticLabel ? `Static unit price (${staticLabel})` : 'Static unit price'
        });

        const plans = (this.paymentPlans.getRawValue?.() as Array<any>) || [];
        plans.forEach((plan, index) => {
            const displayPrice = this.publicPriceAmountLabel(this.parseNumber(plan?.displayPrice));
            options.push({
                value: this.paymentPlanPublicSourceValue(plan, index),
                label: displayPrice
                    ? `Plan ${index + 1} display price (${displayPrice})`
                    : `Plan ${index + 1} display price`
            });
        });
        return options;
    }

    setPublicPriceSource(value: any): void {
        this.publicPriceSource = this.normalizePublicPriceSource(value);
        this.normalizePublicPriceSourceSelection();
        this.touchDraft();
    }

    publicPriceSourceHelpText(): string {
        const issue = this.publicPriceSourceIssue();
        if (issue) return issue;
        if (this.publicPriceSource === 'static') {
            return 'Public cards and property pages will use the unit price field.';
        }
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) {
            return 'Select a payment plan if you want the public listing price to come from a plan display price.';
        }
        const amount = this.publicPriceAmountLabel(this.parseNumber(selected.plan?.displayPrice));
        return amount
            ? `Public cards and property pages will use ${amount} from Plan ${selected.index + 1}.`
            : `Public cards and property pages will use the selected plan once its display price is set.`;
    }

    publicPriceSourceHasIssue(): boolean {
        return Boolean(this.publicPriceSourceIssue());
    }

    private publicPriceSourceIssue(): string | null {
        if (this.planMode === 'none' || this.publicPriceSource === 'static') return null;
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) {
            return 'Select a valid payment plan for the public listing price or switch back to the static unit price.';
        }
        if (this.parseNumber(selected.plan?.displayPrice) === null) {
            return `Plan ${selected.index + 1} is selected for the public listing price. Enter its Public display price first.`;
        }
        return null;
    }

    private publicPriceAmountLabel(value: number | null): string {
        if (value === null) return '';
        const formatted = this.formatNumberNoDecimals(value);
        const currency = this.priceCurrencyLabel;
        return currency ? `${formatted} ${currency}` : formatted;
    }

    private paymentPlanPublicSourceValue(plan: any, index: number): string {
        const planId = String(plan?.id || '').trim();
        return planId ? `plan:${planId}` : `index:${index}`;
    }

    private normalizePublicPriceSource(value: any): string {
        const raw = String(value || '').trim();
        return raw || 'static';
    }

    private getSelectedPublicPricePlan(plans: Array<any> | null | undefined, sourceValue: string = this.publicPriceSource): { plan: any; index: number } | null {
        const source = this.normalizePublicPriceSource(sourceValue);
        if (source === 'static') return null;
        const list = Array.isArray(plans) ? plans : [];
        if (source.startsWith('plan:')) {
            const planId = source.slice(5);
            const index = list.findIndex((plan) => String(plan?.id || '').trim() === planId);
            return index >= 0 ? { plan: list[index], index } : null;
        }
        if (source.startsWith('index:')) {
            const index = Number.parseInt(source.slice(6), 10);
            return Number.isInteger(index) && index >= 0 && index < list.length ? { plan: list[index], index } : null;
        }
        return null;
    }

    private sourceValueFromPaymentPlanId(planId: string | null): string {
        const normalizedPlanId = String(planId || '').trim();
        if (!normalizedPlanId) return 'static';
        const plans = (this.paymentPlans.getRawValue?.() as Array<any>) || [];
        const index = plans.findIndex((plan) => String(plan?.id || '').trim() === normalizedPlanId);
        return index >= 0 ? this.paymentPlanPublicSourceValue(plans[index], index) : 'static';
    }

    private normalizePublicPriceSourceSelection(): void {
        if (this.planMode === 'none') {
            this.publicPriceSource = 'static';
            return;
        }
        if (this.publicPriceSource === 'static') return;
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) {
            this.publicPriceSource = 'static';
        }
    }

    private rebasePublicPriceSourceAfterPlanRemoval(removedIndex: number): void {
        const source = this.normalizePublicPriceSource(this.publicPriceSource);
        if (!source.startsWith('index:')) {
            this.normalizePublicPriceSourceSelection();
            return;
        }
        const currentIndex = Number.parseInt(source.slice(6), 10);
        if (!Number.isInteger(currentIndex)) {
            this.publicPriceSource = 'static';
            return;
        }
        if (currentIndex === removedIndex) {
            this.publicPriceSource = 'static';
            return;
        }
        if (currentIndex > removedIndex) {
            this.publicPriceSource = `index:${currentIndex - 1}`;
        }
        this.normalizePublicPriceSourceSelection();
    }

    private resolveSelectedPublicPricePlanId(sourceValue: string, currentPlans: Array<any>, persistedPlans: Array<any>): string | null {
        const selected = this.getSelectedPublicPricePlan(currentPlans, sourceValue);
        if (!selected) return null;
        const selectedId = String(selected.plan?.id || '').trim();
        if (selectedId) return selectedId;
        const signature = this.paymentPlanSignature(selected.plan, this.getBasePrice(), 'form');
        const match = (persistedPlans || []).find((plan) => this.paymentPlanSignature(plan, this.getBasePrice(), 'persisted') === signature);
        return match?.id ? String(match.id) : null;
    }

    private paymentPlanSignature(plan: any, price: number | null, inputMode: 'form' | 'persisted'): string {
        const downpayment = inputMode === 'form'
            ? (this.resolveAmountFromPercent(this.parseNumber(plan?.downPayment ?? plan?.downPaymentPercent)) ?? this.parseNumber(plan?.downPayment))
            : (this.parseNumber(plan?.downPaymentPercent) !== null
                ? (this.resolveAmountFromPercent(this.parseNumber(plan?.downPaymentPercent)) ?? this.parseNumber(plan?.downPaymentPercent))
                : this.parseNumber(plan?.downPayment));
        const reservationAmount = inputMode === 'form'
            ? (this.resolveAmountFromPercent(this.parseNumber(plan?.reservationAmount ?? plan?.reservationPercent)) ?? this.parseNumber(plan?.reservationAmount))
            : (this.parseNumber(plan?.reservationPercent) !== null
                ? (this.resolveAmountFromPercent(this.parseNumber(plan?.reservationPercent)) ?? this.parseNumber(plan?.reservationPercent))
                : this.parseNumber(plan?.reservationAmount));
        const termsFromRows = this.buildPaymentTermsFromExtraRows(plan, price);
        const extraRows = Array.isArray(plan?.extraRows) ? plan.extraRows : [];
        const managed = plan?.paymentTermsManaged === true || String(plan?.paymentTermsManaged || '').toLowerCase() === 'true';
        const shouldClearTerms = managed && extraRows.length === 0;
        const rawTerms = typeof plan?.paymentTerms === 'string' ? plan.paymentTerms.trim() : '';
        const paymentTerms = termsFromRows !== null ? termsFromRows : shouldClearTerms ? null : (rawTerms || null);
        return JSON.stringify({
            totalPeriod: this.parseInt(plan?.totalPeriod),
            paymentCycle: this.parseInt(plan?.paymentCycle),
            downpayment,
            reservationAmount,
            installmentAmount: this.parseNumber(plan?.installmentAmount),
            numberOfInstallments: this.parseInt(plan?.numberOfInstallments),
            displayPrice: this.parseNumber(plan?.displayPrice),
            paymentTerms
        });
    }

	    private buildPaymentTermsFromExtraRows(plan: any, price: number | null): string | null {
	        const rows = Array.isArray(plan?.extraRows) ? plan.extraRows : [];
	        if (!rows.length) return null;
	        const currency = this.priceCurrencyLabel;
	        const lines = rows
	            .map((row: any) => {
	                if (!row || typeof row !== 'object') return null;
	                const label = String(row.label || '').trim();
	                const percent = this.parseNumber(row.percent);
	                if (!label || percent === null) return null;
	                const amount = price !== null ? (price * percent) / 100 : null;
	                const amountLabel = amount !== null ? `${this.formatNumberNoDecimals(amount)}${currency ? ` ${currency}` : ''}` : null;
	                const pctLabel = `(${this.formatPercent(percent)}%)`;
	                return amountLabel ? `${label}: ${pctLabel} ${amountLabel}` : `${label}: ${pctLabel}`;
	            })
	            .filter(Boolean) as string[];

	        return lines.length ? lines.join('\n') : null;
	    }

	    private parseExtraRowsFromPaymentTerms(terms: string | null | undefined): Array<{ label: string; percent: number }> {
	        if (!terms) return [];
	        const lines = String(terms)
	            .split(/\r?\n/g)
	            .map((l) => l.trim())
	            .filter(Boolean);
	        const rows: Array<{ label: string; percent: number }> = [];
	        for (const line of lines) {
	            const match = line.match(/^\s*([^:]+)\s*:\s*\(\s*([0-9]+(?:\.[0-9]+)?)\s*%\s*\)/i);
	            if (!match) continue;
	            const label = String(match[1] || '').trim();
	            const percent = this.parseNumber(match[2]);
	            if (!label || percent === null) continue;
	            rows.push({ label, percent });
	        }
	        return rows;
	    }

    private parseInt(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private normalizeDateForInput(value: unknown): string {
        if (value instanceof Date) {
            const ts = value.getTime();
            return Number.isFinite(ts) ? value.toISOString().slice(0, 10) : '';
        }
        if (value === null || value === undefined) return '';
        const raw = String(value).trim();
        if (!raw) return '';
        const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch?.[1]) return isoMatch[1];
        const parsed = Date.parse(raw);
        if (!Number.isFinite(parsed)) return '';
        return new Date(parsed).toISOString().slice(0, 10);
    }

    private applyCountryCurrency(country: string | null | undefined, force = false) {
        const normalized = this.normalizeCountryCode(country);
        if (!normalized || !this.currencies.length) return;
        if (this.currencyChangedByUser && !force) return;
        // A saved currency is explicit even before the user touches the selector.
        // Changing the country must not relabel existing financial amounts.
        if (this.lastCurrencyId && !force) return;
        const desired = COUNTRY_CURRENCY_MAP[normalized];
        if (!desired) return;
        const match = this.currencies.find(
            (c) =>
                (c.CODE || c.NAME || '').toString().toUpperCase() === desired ||
                (c.CODE || c.NAME || '').toString().toUpperCase() === desired.toUpperCase()
        );
        if (match) {
            this.updatingCurrencyFromCountry = true;
            this.form.patchValue({ currencyId: match.ROWID }, { emitEvent: false });
            this.updatingCurrencyFromCountry = false;
            this.lastCurrencyId = this.normalizeCurrencyCode(match.ROWID);
        }
    }

    private initAreaToggleWatchers() {
        ([
            ['buaEnabled', 'bua'],
            ['totalAreaEnabled', 'totalArea'],
            ['gardenAreaEnabled', 'gardenArea'],
            ['balconyAreaEnabled', 'balconyArea'],
            ['terraceAreaEnabled', 'terraceArea']
        ] as Array<[string, string]>).forEach(([flag, field]) => {
            this.form.get(flag)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.onAreaToggle(flag, field));
            this.onAreaToggle(flag, field);
        });
    }

    onAreaToggle(flagControl: string, targetControl: string) {
        const enabled = this.form.get(flagControl)?.value !== false;
        const control = this.form.get(targetControl);
        if (!control) return;
        if (enabled) {
            control.enable({ emitEvent: false });
        } else {
            control.disable({ emitEvent: false });
            control.reset('');
        }
    }

    private resolveDiscountChoice(discountRate?: number | null): number | 'custom' | '' {
        const numeric = this.parseNumber(discountRate);
        if (numeric === null) return '';
        const match = this.discountOptions.find((opt) => opt !== 'custom' && Number(opt) === Number(numeric));
        if (match !== undefined && match !== 'custom') {
            return Number(match);
        }
        return 'custom';
    }

    private resolveDiscountValue(formValue: any): number | null {
        const choice = formValue.discountRateChoice;
        if (choice === 'custom') {
            return this.parseNumber(formValue.customDiscountRate);
        }
        const numericChoice = this.parseNumber(choice);
        if (numericChoice !== null) {
            return numericChoice;
        }
        return this.parseNumber(formValue.discountRate);
    }

    get formattedPricePreview(): string | null {
        const raw = this.parseNumber(this.form.value.price);
        if (raw === null || raw === undefined || !Number.isFinite(raw)) return null;
        const rounded = Math.round(raw * 100) / 100;
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(rounded);
    }

    private normalizeStatusForPayload(rawStatus: unknown): string {
        const current = (rawStatus || '').toString().trim();
        if (current !== 'ReservedOrHold') return current;
        const original = ((this.originalPayloadSnapshot['status'] as any) || this.property?.status || '').toString().trim();
        if (original === 'Hold' || original === 'Reserved') return original;
        return 'Hold';
    }

    private applyNormalizedPropertyPatch(snapshot: any, normalized: Record<string, unknown>): Property {
        const next: any = { ...(snapshot || {}) };
        const n: any = normalized || {};

        if (typeof n.title === 'string') next.title = n.title;
        if ('tag_line' in n) next.tagLine = n.tag_line === null ? null : String(n.tag_line || '').trim() || null;
        if ('description' in n) next.description = n.description === null ? null : String(n.description || '').trim() || null;
        if ('status' in n) next.status = typeof n.status === 'string' ? n.status : String(n.status || '');
        if ('price' in n) next.price = typeof n.price === 'number' ? n.price : n.price === null ? null : Number(n.price);
        if ('discount_rate' in n) next.discountRate = n.discount_rate === null ? null : Number(n.discount_rate);
        if ('ready_to_move' in n) next.readyToMove = n.ready_to_move === null ? null : Boolean(n.ready_to_move);
        if ('delivery_date' in n) next.deliveryDate = n.delivery_date === null ? null : String(n.delivery_date || '').trim() || null;
        if ('finishing_type' in n) next.finishingType = n.finishing_type === null ? null : String(n.finishing_type || '').trim() || null;
        if ('furnish_type' in n) next.furnishType = n.furnish_type === null ? null : String(n.furnish_type || '').trim() || null;
        if ('maintenance_fee_mode' in n) next.maintenanceFeeMode = n.maintenance_fee_mode === null ? null : String(n.maintenance_fee_mode || '').trim() || null;
        if ('maintenance_fees' in n) next.maintenanceFees = n.maintenance_fees === null ? null : Number(n.maintenance_fees);
        if ('tags' in n) {
            const raw = n.tags === null ? '' : String(n.tags || '');
            next.tags = raw
                ? raw
                    .split(',')
                    .map((t: string) => t.trim())
                    .filter((t: string) => Boolean(t))
                : [];
        }
        if ('currency_code' in n) {
            const code = String(n.currency_code || '').trim().toUpperCase();
            if (code) {
                next.currencyId = code;
                next.currency = code;
                const currency = this.currencies.find((c) => c.ROWID === code || (c.CODE || '').toUpperCase() === code);
                next.currencyInfo = {
                    code: currency?.CODE || code,
                    symbol: currency?.SYMBOL || null,
                    name: currency?.NAME || null
                };
            }
        }

        if ('project_id' in n) next.project_id = n.project_id ? String(n.project_id) : null;
        if ('phase_id' in n) next.phaseId = n.phase_id ? String(n.phase_id) : null;
        if ('category_id' in n) {
            next.categoryId = n.category_id ? String(n.category_id) : null;
            const match = next.categoryId
                ? (this.categories || []).find((c) => String(c?.ROWID || '') === String(next.categoryId))
                : null;
            const name = (match?.NAME || '').toString().trim();
            if (name) {
                next.categoryName = name;
                next.type = name;
            }
        }

        if ('location_address' in n) {
            next.locationAddress = n.location_address === null ? null : String(n.location_address || '').trim() || null;
        }

        if ('location_link' in n) {
            next.locationLink = n.location_link === null ? null : String(n.location_link || '').trim() || null;
        }

        if ('country_code' in n) {
            const code = n.country_code ? String(n.country_code).trim().toUpperCase() : null;
            next.countryCode = code;
            if (code) {
                const match = COUNTRIES.find((c) => c.code === code);
                const name = match?.name || code;
                next.countryName = name;
                next.country = name;
            }
        }

        if ('bua' in n) next.bua = typeof n.bua === 'number' ? n.bua : n.bua === null ? null : Number(n.bua);
        if ('total_area' in n) next.totalArea = typeof n.total_area === 'number' ? n.total_area : n.total_area === null ? null : Number(n.total_area);
        if ('garden_area' in n) next.gardenArea = typeof n.garden_area === 'number' ? n.garden_area : Number(n.garden_area ?? 0);
        if ('balcony_area' in n) next.balconyArea = typeof n.balcony_area === 'number' ? n.balcony_area : n.balcony_area === null ? null : Number(n.balcony_area);
        if ('terrace_area' in n) next.terraceArea = typeof n.terrace_area === 'number' ? n.terrace_area : Number(n.terrace_area ?? 0);

        if ('bedrooms' in n) next.bedrooms = typeof n.bedrooms === 'number' ? n.bedrooms : n.bedrooms === null ? null : Number(n.bedrooms);
        if ('bathrooms' in n) next.bathrooms = typeof n.bathrooms === 'number' ? n.bathrooms : n.bathrooms === null ? null : Number(n.bathrooms);

        if ('unit_number' in n) {
            const unit = n.unit_number === null ? null : String(n.unit_number || '').trim();
            next.unitNumber = unit || null;
        }

        if ('floor' in n) {
            const floor = n.floor === null ? null : String(n.floor || '').trim();
            next.floor = floor || null;
        }

        if ('number_of_floors' in n) {
            const floors = n.number_of_floors === null ? null : String(n.number_of_floors || '').trim();
            next.numberOfFloors = floors || null;
        }

        next.area = next.totalArea ?? next.bua ?? next.area ?? null;
        return next as Property;
    }

    private normalizePayloadFromFormValue(value: any): Record<string, unknown> {
        const coerceNumberOrNull = (val: any) => (val === null || val === undefined || val === '' ? null : this.parseNumber(val));
        const coerceIntOrNull = (val: any) => (val === null || val === undefined || val === '' ? null : this.parseInt(val));
        const isEnabled = (flag: any) => flag !== false;
        const readyToMove =
            value.readyToMove === 'true'
                ? true
                : value.readyToMove === 'false'
                    ? false
                    : null;
        return {
            title: value.title?.toString().trim() || '',
            tag_line: value.tagLine?.toString().trim() || null,
            description: value.description?.toString().trim() || null,
            project_id: value.projectId ? String(value.projectId) : null,
            phase_id: value.phaseId ? String(value.phaseId) : null,
            category_id: value.categoryId ? String(value.categoryId) : null,
            country_code: value.country ? this.normalizeCountryCode(String(value.country)) : null,
            status: this.normalizeStatusForPayload(value.status),
            price: coerceNumberOrNull(value.price),
            discount_rate: this.resolveDiscountValue(value),
            currency_code: value.currencyId || 'USD',
            bua: isEnabled(value.buaEnabled) ? coerceNumberOrNull(value.bua) : null,
            bua_uom: String(value.buaUnit || 'm²').trim() || 'm²',
            total_area: isEnabled(value.totalAreaEnabled) ? coerceNumberOrNull(value.totalArea) : null,
            total_area_uom: String(value.totalAreaUnit || 'm²').trim() || 'm²',
            garden_area: isEnabled(value.gardenAreaEnabled) ? (coerceNumberOrNull(value.gardenArea) ?? 0) : 0,
            garden_area_uom: String(value.gardenAreaUnit || 'm²').trim() || 'm²',
            balcony_area: isEnabled(value.balconyAreaEnabled) ? coerceNumberOrNull(value.balconyArea) : null,
            balcony_area_uom: String(value.balconyAreaUnit || 'm²').trim() || 'm²',
            terrace_area: isEnabled(value.terraceAreaEnabled) ? (coerceNumberOrNull(value.terraceArea) ?? 0) : 0,
            terrace_area_uom: String(value.terraceAreaUnit || 'm²').trim() || 'm²',
            bedrooms: coerceIntOrNull(value.bedrooms),
            bathrooms: coerceIntOrNull(value.bathrooms),
            unit_number: value.unitNumber ? String(value.unitNumber).trim() : null,
            floor: value.floor !== null && value.floor !== undefined && String(value.floor).trim() !== '' ? String(value.floor).trim() : null,
            number_of_floors:
                value.numberOfFloors !== null && value.numberOfFloors !== undefined && String(value.numberOfFloors).trim() !== ''
                    ? String(value.numberOfFloors).trim()
                    : null,
            ready_to_move: readyToMove,
            delivery_date: value.deliveryDate ? String(value.deliveryDate).trim() || null : null,
            finishing_type: value.finishingType ? String(value.finishingType).trim() || null : null,
            furnish_type: value.furnishType ? String(value.furnishType).trim() || null : null,
            card_badge_1: value.cardBadge1 ? String(value.cardBadge1).trim() || null : null,
            card_badge_2: value.cardBadge2 ? String(value.cardBadge2).trim() || null : null,
            maintenance_fee_mode: value.maintenanceFeeMode ? String(value.maintenanceFeeMode).trim() || null : null,
            maintenance_fees: coerceNumberOrNull(value.maintenanceFees),
            tags: value.tags ? String(value.tags).trim() || null : null,
            location_address: value.locationAddress ? String(value.locationAddress).trim() || null : null,
            location_link: value.locationLink ? String(value.locationLink).trim() || null : null
        };
    }

    private normalizePayloadFromPropertyData(property: Property | null): Record<string, unknown> {
        if (!property) return {};
        return {
            title: property.title?.toString().trim() || '',
            tag_line: property.tagLine ? String(property.tagLine).trim() : null,
            description: property.description ? String(property.description).trim() : null,
            project_id: property.project_id ? String(property.project_id) : null,
            phase_id: property.phaseId ? String(property.phaseId) : property.phase?.id ? String(property.phase.id) : null,
            category_id: property.categoryId ? String(property.categoryId) : null,
            country_code: property.countryCode ? String(property.countryCode).trim().toUpperCase() : null,
            status: property.status ? String(property.status).trim() : '',
            price: typeof property.price === 'number' ? property.price : property.price === null ? null : Number(property.price),
            discount_rate: typeof property.discountRate === 'number' ? property.discountRate : property.discountRate === null ? null : Number(property.discountRate),
            currency_code: property.currencyId ? String(property.currencyId).trim().toUpperCase() : String(property.currency || 'USD').trim().toUpperCase(),
            bua: typeof property.bua === 'number' ? property.bua : property.bua === null ? null : Number(property.bua),
            bua_uom: property.buaUnit ? String(property.buaUnit).trim() : 'm²',
            total_area: typeof property.totalArea === 'number' ? property.totalArea : property.totalArea === null ? null : Number(property.totalArea),
            total_area_uom: property.totalAreaUnit ? String(property.totalAreaUnit).trim() : 'm²',
            garden_area: typeof property.gardenArea === 'number' ? property.gardenArea : Number(property.gardenArea ?? 0),
            garden_area_uom: property.gardenAreaUnit ? String(property.gardenAreaUnit).trim() : 'm²',
            balcony_area: typeof property.balconyArea === 'number' ? property.balconyArea : property.balconyArea === null ? null : Number(property.balconyArea),
            balcony_area_uom: property.balconyAreaUnit ? String(property.balconyAreaUnit).trim() : 'm²',
            terrace_area: typeof property.terraceArea === 'number' ? property.terraceArea : Number(property.terraceArea ?? 0),
            terrace_area_uom: property.terraceAreaUnit ? String(property.terraceAreaUnit).trim() : 'm²',
            bedrooms: typeof property.bedrooms === 'number' ? property.bedrooms : property.bedrooms === null ? null : Number(property.bedrooms),
            bathrooms: typeof property.bathrooms === 'number' ? property.bathrooms : property.bathrooms === null ? null : Number(property.bathrooms),
            unit_number: property.unitNumber ? String(property.unitNumber).trim() : null,
            floor: property.floor ? String(property.floor).trim() : null,
            number_of_floors: property.numberOfFloors ? String(property.numberOfFloors).trim() : null,
            ready_to_move: property.readyToMove === true ? true : property.readyToMove === false ? false : null,
            delivery_date: this.normalizeDateForInput(property.deliveryDate) || null,
            finishing_type: property.finishingType ? String(property.finishingType).trim() : null,
            furnish_type: property.furnishType ? String(property.furnishType).trim() : null,
            card_badge_1: property.cardBadge1 ? String(property.cardBadge1).trim() : null,
            card_badge_2: property.cardBadge2 ? String(property.cardBadge2).trim() : null,
            maintenance_fee_mode: property.maintenanceFeeMode ? String(property.maintenanceFeeMode).trim() : null,
            maintenance_fees: typeof property.maintenanceFees === 'number' ? property.maintenanceFees : property.maintenanceFees === null ? null : Number(property.maintenanceFees),
            tags: Array.isArray(property.tags) ? property.tags.join(', ') : null,
            location_address: property.locationAddress ? String(property.locationAddress).trim() : null,
            location_link: property.locationLink ? String(property.locationLink).trim() : null
        };
    }

    private convertPriceAndPlans(from: string, to: string): boolean {
        // Check FX before mutating any field: display fallback is safe for viewing,
        // but must never silently relabel stored amounts when editing.
        const probe = this.currencyService.convertForDisplay(1, from, to);
        if (probe.currency !== to) return false;
        const convert = (value: unknown): string | null => {
            const amount = this.parseNumber(value);
            if (amount === null) return null;
            const converted = this.currencyService.convertForDisplay(amount, from, to).amount;
            return String(Math.round(converted * 100) / 100);
        };
        const price = convert(this.form.value.price);
        if (price !== null) this.form.patchValue({ price }, { emitEvent: false });
        for (const plan of this.paymentPlans.controls) {
            // downPayment, reservationAmount and extra rows contain percentages.
            // They remain unchanged; save() derives their new monetary amounts.
            for (const field of ['displayPrice', 'installmentAmount']) {
                const amount = convert(plan.get(field)?.value);
                if (amount !== null) plan.patchValue({ [field]: amount }, { emitEvent: false });
            }
        }
        return true;
    }

    private getBasePrice(): number | null {
        const priceFromForm = this.parseNumber(this.form.value.price);
        if (priceFromForm !== null) return priceFromForm;
        return this.property?.price ?? null;
    }

    private resolveAmountFromPercent(percent: number | null): number | null {
        const price = this.getBasePrice();
        if (!price || percent === null || percent === undefined) return null;
        return (price * percent) / 100;
    }

    private resolvePercentFromAmount(amount: number | null, basePrice?: number | null): number | null {
        const price = basePrice ?? this.getBasePrice();
        if (!price || amount === null || amount === undefined) return null;
        if (price === 0) return null;
        return (amount / price) * 100;
    }

    private normalizeCurrencyCode(value: unknown): string | null {
        if (value === null || value === undefined) return null;
        const raw = String(value || '').trim().toUpperCase();
        return raw || null;
    }

    private normalizeCountryCode(value: string | null | undefined): string {
        if (!value) return '';
        const raw = value.toString().trim();
        if (!raw) return '';
        const upper = raw.toUpperCase();
        const strip = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
        const stripped = strip(raw);
        const match = COUNTRIES.find(
            (c) => c.code === upper || c.code === stripped || strip(c.name) === stripped || c.name.toUpperCase() === upper
        );
        if (match) return match.code;
        // Preserve raw two-letter codes or country names even if not in our list
        return upper.length === 2 ? upper : raw;
    }

    private normalizeImages(list: PropertyImage[], respectInputOrder = false): PropertyImage[] {
        const base = respectInputOrder
            ? [...list]
            : [...list].sort((a, b) => {
                const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });
        if (base.length && !base.some(img => img.isMain)) {
            base[0] = { ...base[0], isMain: true };
        }
        return base;
    }

    private assignImageOrder(list: PropertyImage[]): PropertyImage[] {
        return list.map((img, idx) => ({ ...img, order: idx + 1 }));
    }

    private setImages(list: PropertyImage[], respectInputOrder = false) {
        this.images = this.assignImageOrder(this.normalizeImages(list, respectInputOrder));
        this.touchDraft();
    }

    private buildDraftStorageKey(propertyId: string): string {
        return `nibni:draft:property-edit:${propertyId}`;
    }

    private touchDraft(): void {
        if (!this.propertyId || !this.draftStorageKey) return;
        if (this.initializingForm || this.suppressDraftWrites) return;
        this.draftTrigger$.next();
    }

    private persistDraft(): void {
        this.touchDraft();
    }

    private readDraft(): PropertyEditDraftV1 | null {
        if (!this.propertyId || !this.draftStorageKey) return null;
        try {
            const raw = localStorage.getItem(this.draftStorageKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || parsed.version !== 1) return null;
            if (String(parsed.propertyId || '') !== String(this.propertyId)) return null;
            return parsed as PropertyEditDraftV1;
        } catch {
            return null;
        }
    }

    private writeDraft(): void {
        if (!this.propertyId || !this.draftStorageKey) return;
        if (this.initializingForm || this.suppressDraftWrites) return;

        const draft: PropertyEditDraftV1 = {
            version: 1,
            propertyId: this.propertyId,
            updatedAt: new Date().toISOString(),
            formValue: (this.form.getRawValue() || {}) as any,
            selectedFeatures: Array.from(this.selectedFeatures.entries()).map(([k, v]) => [String(k), String(v || '')]),
            landmarks: (this.landmarks.getRawValue() as any) || [],
            paymentPlans: (this.paymentPlans.getRawValue() as any) || [],
            planMode: this.planMode,
            publicPriceSource: this.publicPriceSource,
            images: Array.isArray(this.images) ? this.images : []
        };

        try {
            localStorage.setItem(this.draftStorageKey, JSON.stringify(draft));
        } catch {
            // ignore storage failures
        }
    }

    private clearDraft(): void {
        if (!this.draftStorageKey) return;
        try {
            localStorage.removeItem(this.draftStorageKey);
        } catch {
            // ignore
        }
    }

    private async maybeRestoreDraft(propertyId: string): Promise<void> {
        if (!propertyId || !this.propertyId) return;
        if (this.draftRestoreCheckedId === propertyId) return;
        this.draftRestoreCheckedId = propertyId;

        const draft = this.readDraft();
        if (!draft) return;

        const shouldRestore = await this.confirm.confirm({
            title: 'Restore draft?',
            message: 'We found unsaved changes from your previous session. Restore them?',
            confirmText: 'Restore',
            cancelText: 'Discard'
        });

        if (!shouldRestore) {
            this.clearDraft();
            return;
        }

        this.applyDraft(draft);
        this.notifications.showInfo('Draft restored').subscribe();
    }

    private applyDraft(draft: PropertyEditDraftV1): void {
        this.suppressDraftWrites = true;
        try {
            const formValue = draft?.formValue && typeof draft.formValue === 'object' ? draft.formValue : {};
            this.form.patchValue(formValue, { emitEvent: true });

            const selected = Array.isArray(draft?.selectedFeatures) ? draft.selectedFeatures : [];
            this.selectedFeatures = new Map(selected.map(([k, v]) => [String(k), String(v || '')]));

            const landmarks = Array.isArray(draft?.landmarks) ? draft.landmarks : [];
            this.landmarks.clear();
	            landmarks.forEach((lm) => {
	                this.landmarks.push(this.fb.group({
	                    id: [String((lm as any)?.id || '')],
	                    type: [String((lm as any)?.type || 'custom')],
	                    name: [String((lm as any)?.name || '')],
	                    distance: [String((lm as any)?.distance || '')],
	                    distanceUnit: [this.normalizeDistanceUom((lm as any)?.distanceUnit ?? (lm as any)?.distanceUom ?? 'm')],
	                    timeMinutes: [String((lm as any)?.timeMinutes || '')]
	                }));
	            });
            if (this.landmarks.length === 0) {
                this.landmarks.push(this.createLandmarkGroup());
            }

	            const plans = Array.isArray(draft?.paymentPlans) ? draft.paymentPlans : [];
	            this.paymentPlans.clear();
	            plans.forEach((plan) => {
	                const group = this.createPaymentPlanGroup();
	                const terms = (plan as any)?.paymentTerms ?? (plan as any)?.payment_terms ?? '';
	                const extraRows = Array.isArray((plan as any)?.extraRows) ? (plan as any).extraRows : [];
	                const managedRaw = (plan as any)?.paymentTermsManaged;
	                const managed = managedRaw === true || String(managedRaw || '').toLowerCase() === 'true' || extraRows.length > 0;
	                group.patchValue(
	                    {
	                        id: String((plan as any)?.id || ''),
	                        paymentTerms: terms ? String(terms) : '',
	                        paymentTermsManaged: managed,
	                        totalPeriod: (plan as any)?.totalPeriod ?? '',
	                        paymentCycle: (plan as any)?.paymentCycle ?? '',
	                        downPayment: (plan as any)?.downPayment ?? '',
	                        reservationAmount: (plan as any)?.reservationAmount ?? '',
                            displayPrice: (plan as any)?.displayPrice ?? '',
	                        installmentAmount: (plan as any)?.installmentAmount ?? '',
	                        numberOfInstallments: (plan as any)?.numberOfInstallments ?? ''
	                    },
	                    { emitEvent: false }
	                );
	                const rowsArray = group.get('extraRows') as FormArray | null;
	                if (rowsArray) {
	                    rowsArray.clear();
	                    extraRows.forEach((row: any) => {
	                        rowsArray.push(this.createPaymentPlanRowGroup({ label: row?.label, percent: row?.percent }));
	                    });
	                }
	                this.paymentPlans.push(group);
	            });
	            if (this.paymentPlans.length === 0) {
	                this.paymentPlans.push(this.createPaymentPlanGroup());
	            }

            const mode = draft?.planMode;
            if (mode === 'none' || mode === 'auto' || mode === 'manual') {
                this.planMode = mode;
            }
            this.publicPriceSource = this.normalizePublicPriceSource(draft?.publicPriceSource ?? this.publicPriceSource);
            this.normalizePublicPriceSourceSelection();

            if (Array.isArray(draft?.images)) {
                this.setImages(draft.images, true);
            }

            this.onProjectChange();
        } finally {
            this.suppressDraftWrites = false;
        }
    }

    private mergeImages(current: PropertyImage[], incoming: PropertyImage[] | null | undefined): PropertyImage[] {
        const next = Array.isArray(incoming) ? [...incoming] : [];
        const byKey = new Map<string, PropertyImage>();

        const add = (img: PropertyImage) => {
            const key = img.id ? `id:${img.id}` : `url:${img.url}`;
            if (!key) return;
            const existing = byKey.get(key);
            byKey.set(key, { ...existing, ...img });
        };

        next.forEach(add);
        (current || []).forEach(add);

        return Array.from(byKey.values());
    }

    private deriveImageLabel(source: string | null | undefined, index: number): string {
        if (!source) return `Image ${index + 1}`;
        const trimmed = source.toString().trim();
        if (!trimmed) return `Image ${index + 1}`;
        try {
            const url = new URL(trimmed);
            const pathname = url.pathname.split('/').pop() || '';
            const withoutExt = pathname.replace(/\.[^.]+$/, '');
            if (withoutExt) return withoutExt;
        } catch {
            // not a URL; fall through
        }
        const withoutExt = trimmed.replace(/\.[^.]+$/, '');
        return withoutExt || `Image ${index + 1}`;
    }

    onFeatureCheckboxChange(id: string, event: Event) {
        const target = event.target as HTMLInputElement | null;
        this.toggleFeature(id, Boolean(target?.checked));
    }

    onFeatureValueInput(id: string, event: Event) {
        const target = event.target as HTMLInputElement | null;
        this.updateFeatureValue(id, target?.value ?? '');
    }

    private createLandmarkGroup(): FormGroup {
        return this.fb.group({
            id: [''],
            type: ['custom'],
            name: [''],
            distance: [''],
            distanceUnit: ['m'],
            timeMinutes: ['']
        });
    }

    private normalizeDistanceUom(value: unknown): 'm' | 'km' | 'mile' {
        const raw = String(value ?? '').trim().toLowerCase();
        if (raw === 'km' || raw === 'kilometer' || raw === 'kilometre') return 'km';
        if (raw === 'mile' || raw === 'mi') return 'mile';
        return 'm';
    }

    private distanceToMeters(value: number | null, unit: 'm' | 'km' | 'mile'): number | null {
        if (value === null || value === undefined) return null;
        if (!Number.isFinite(value)) return null;
        if (unit === 'km') return Math.round(value * 1000);
        if (unit === 'mile') return Math.round(value * 1609.344);
        return Math.round(value);
    }

    private metersToDistanceValue(meters: number | null, unit: 'm' | 'km' | 'mile'): number | null {
        if (meters === null || meters === undefined) return null;
        if (!Number.isFinite(meters)) return null;
        if (unit === 'km') return meters / 1000;
        if (unit === 'mile') return meters / 1609.344;
        return meters;
    }

    private formatDistanceInput(value: number | null, unit: 'm' | 'km' | 'mile'): string {
        if (value === null || value === undefined) return '';
        const rounded = unit === 'm' ? Math.round(value) : Math.round(value * 100) / 100;
        return Number.isFinite(rounded) ? String(rounded) : '';
    }

	    private createPaymentPlanGroup(): FormGroup {
	        return this.fb.group({
	            id: [''],
	            paymentTerms: [''],
	            paymentTermsManaged: [false],
	            totalPeriod: [''],
	            paymentCycle: [''],
	            downPayment: [''],
	            reservationAmount: [''],
	            displayPrice: [''],
	            installmentAmount: [''],
	            numberOfInstallments: [''],
	            extraRows: this.fb.array([])
	        });
	    }

	    private createPaymentPlanRowGroup(row?: { label?: string; percent?: number | string | null }): FormGroup {
	        return this.fb.group({
	            label: [row?.label ?? ''],
	            percent: [row?.percent ?? '']
	        });
	    }

    private populateFeatures(features: Property['features']) {
        this.selectedFeatures.clear();
        (features || []).forEach((feat) => {
            const featureId = (feat as any)?.featureId || (feat as any)?.FEATURE_ID;
            if (featureId) {
                this.selectedFeatures.set(String(featureId), feat?.value || '');
            }
        });
    }

    private captureSnapshots(property: Property) {
        this.originalFeaturesSignature = this.serializeSelectedFeatures();
        this.originalLandmarksSignature = this.serializeLandmarks(this.landmarks.value as any);
        this.originalPaymentPlansSignature = this.serializePaymentPlans(this.paymentPlans.value as any);
        this.originalImagesSignature = this.serializeImages(this.images || []);
    }

    private serializeSelectedFeatures(): string {
        const pairs = Array.from(this.selectedFeatures.entries()).map(([k, v]) => [k, v || '']);
        pairs.sort((a, b) => a[0].localeCompare(b[0]));
        return JSON.stringify(pairs);
    }

    private serializeLandmarks(landmarks: Property['landmarks']): string {
        const normalized = (landmarks || []).map((lm) => {
            const unit = this.normalizeDistanceUom((lm as any)?.distanceUnit ?? (lm as any)?.distanceUom ?? null);
            const distanceValue = this.parseNumber((lm as any)?.distance);
            const distanceMeters = this.parseNumber((lm as any)?.distanceMeters);
            const distanceMeter = distanceValue !== null ? this.distanceToMeters(distanceValue, unit) : (distanceMeters ?? null);
            const timeRaw = this.parseInt((lm as any)?.timeMinutes);
            const timeMinutes = timeRaw !== null && timeRaw > 0 ? timeRaw : null;
            return {
                id: (lm as any)?.id || null,
                name: (lm?.name || '').toString().trim(),
                distanceMeter,
                distanceUom: unit,
                timeMinutes
            };
        }).filter(lm => lm.name || lm.distanceMeter !== null || lm.timeMinutes !== null);
        normalized.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
        return JSON.stringify(normalized);
    }

	    private serializePaymentPlans(plans: Property['paymentPlans']): string {
	        if (this.planMode === 'none') return 'NONE';
	        const normalized = (plans || [])
	            .map((plan: any) => {
	                const extraRows = Array.isArray(plan?.extraRows) ? plan.extraRows : [];
	                const normalizedRows = extraRows
	                    .map((row: any) => {
	                        if (!row || typeof row !== 'object') return null;
	                        const label = String(row.label || '').trim();
	                        const percent = this.parseNumber(row.percent);
	                        const hasPercent = percent !== null;
	                        if (!label && !hasPercent) return null;
	                        return { label: label || null, percent };
	                    })
	                    .filter(Boolean);
	                const managed = plan?.paymentTermsManaged === true || String(plan?.paymentTermsManaged || '').toLowerCase() === 'true';
	                const rawTerms = typeof plan?.paymentTerms === 'string' ? plan.paymentTerms.trim() : null;
	                return {
	                    id: plan?.id || null,
	                    totalPeriod: this.parseInt(plan?.totalPeriod),
	                    paymentCycle: this.parseInt(plan?.paymentCycle),
	                    downPayment: this.parseNumber(plan?.downPayment ?? plan?.downPaymentPercent),
	                    reservationAmount: this.parseNumber(plan?.reservationAmount ?? plan?.reservationPercent),
                        displayPrice: this.parseNumber(plan?.displayPrice),
	                    installmentAmount: this.parseNumber(plan?.installmentAmount),
	                    numberOfInstallments: this.parseInt(plan?.numberOfInstallments),
	                    paymentTermsManaged: managed,
	                    paymentTerms: rawTerms,
	                    extraRows: normalizedRows
	                };
	            })
	            .filter((p: any) =>
	                p.totalPeriod ||
	                p.paymentCycle ||
	                p.installmentAmount ||
	                p.numberOfInstallments ||
	                p.downPayment !== null ||
	                p.reservationAmount !== null ||
                    p.displayPrice !== null ||
	                (Array.isArray(p.extraRows) && p.extraRows.length) ||
	                p.paymentTerms
	            );
	        normalized.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
	        return JSON.stringify(normalized);
	    }

    private serializeImages(images: PropertyImage[]): string {
        const ordered = this.assignImageOrder(images || []).map((img) => ({
            id: img.id || null,
            url: img.url,
            thumb: img.thumb || null,
            alt: img.alt || null,
            order: img.order || null,
            isMain: Boolean(img.isMain)
        }));
        return JSON.stringify(ordered);
    }

    private populateLandmarks(landmarks: Property['landmarks']) {
        this.landmarks.clear();
        if (landmarks && landmarks.length) {
            landmarks.forEach((lm) => {
                const preset = this.landmarkPresets.includes(lm?.name || '') ? lm?.name : 'custom';
                const unit = this.normalizeDistanceUom((lm as any)?.distanceUom ?? null);
                const meters = this.parseNumber((lm as any)?.distanceMeters);
                const value = this.metersToDistanceValue(meters, unit);
                const timeRaw = this.parseInt((lm as any)?.timeMinutes);
                const timeMinutes = timeRaw !== null && timeRaw > 0 ? timeRaw : null;
                this.landmarks.push(this.fb.group({
                    id: [(lm as any)?.id || ''],
                    type: [preset || 'custom'],
                    name: [lm?.name || ''],
                    distance: [this.formatDistanceInput(value, unit)],
                    distanceUnit: [unit],
                    timeMinutes: [timeMinutes !== null ? String(timeMinutes) : '']
                }));
            });
        }
        if (this.landmarks.length === 0) {
            this.landmarks.push(this.createLandmarkGroup());
        }
    }

	    private populatePaymentPlans(plans: Property['paymentPlans']) {
	        this.paymentPlans.clear();
	        if (plans && plans.length) {
	            plans.forEach((plan) => {
	                // Use displayPrice as base for percentage conversion when available,
	                // since downpayment/reservation amounts are calculated against displayPrice.
	                const planDisplayPrice = this.parseNumber(plan?.displayPrice);
	                const planBasePrice = (planDisplayPrice !== null && planDisplayPrice > 0) ? planDisplayPrice : this.getBasePrice();
	                const downPercent = plan?.downPaymentPercent ?? this.resolvePercentFromAmount(this.parseNumber(plan?.downPayment), planBasePrice);
	                const reservePercent = plan?.reservationPercent ?? this.resolvePercentFromAmount(this.parseNumber(plan?.reservationAmount), planBasePrice);
	                const group = this.createPaymentPlanGroup();
	                const terms =
	                    (plan as any)?.paymentTerms ??
	                    (plan as any)?.PAYMENT_TERMS ??
	                    (plan as any)?.payment_terms ??
	                    null;
	                const extraRows = this.parseExtraRowsFromPaymentTerms(terms);
	                group.patchValue(
	                    {
	                        id: plan?.id || '',
	                        paymentTerms: terms ? String(terms) : '',
	                        paymentTermsManaged: extraRows.length > 0,
	                        totalPeriod: plan?.totalPeriod ?? '',
	                        paymentCycle: plan?.paymentCycle ?? '',
	                        downPayment: downPercent ?? '',
	                        reservationAmount: reservePercent ?? '',
	                        displayPrice: plan?.displayPrice ?? '',
	                        installmentAmount: plan?.installmentAmount ?? '',
	                        numberOfInstallments: plan?.numberOfInstallments ?? ''
	                    },
	                    { emitEvent: false }
	                );
	                const rowsArray = group.get('extraRows') as FormArray | null;
	                if (rowsArray) {
	                    rowsArray.clear();
	                    extraRows.forEach((r) => rowsArray.push(this.createPaymentPlanRowGroup(r)));
	                }
	                this.paymentPlans.push(group);
	            });
	        }
	        if (this.paymentPlans.length === 0) {
	            this.paymentPlans.push(this.createPaymentPlanGroup());
	        }
	        this.planMode = plans && plans.length ? 'manual' : 'none';
            this.normalizePublicPriceSourceSelection();
	    }

	    private validatePaymentPlans(): string | null {
	        if (this.planMode === 'none') return null;
	        const plans = this.paymentPlans.value as Array<any>;
	        for (let i = 0; i < plans.length; i++) {
	            const plan = plans[i];
	            const extraRows = Array.isArray(plan?.extraRows) ? plan.extraRows : [];
	            const hasExtraRowValue = extraRows.some((row: any) => {
	                if (!row || typeof row !== 'object') return false;
	                const label = String(row?.label || '').trim();
	                const percentRaw = row?.percent;
	                const hasPercent = percentRaw !== null && percentRaw !== undefined && String(percentRaw).trim() !== '';
	                return Boolean(label || hasPercent);
	            });
	            const hasValue = plan.paymentCycle || plan.installmentAmount || plan.numberOfInstallments || plan.totalPeriod || plan.downPayment || plan.reservationAmount || this.parseNumber(plan.displayPrice) !== null || hasExtraRowValue;
	            if (hasValue) {
	                const downPct = this.parseNumber(plan.downPayment);
	                const reservePct = this.parseNumber(plan.reservationAmount);
	                if ((downPct !== null && (downPct < 0 || downPct > 100)) || (reservePct !== null && (reservePct < 0 || reservePct > 100))) {
	                    return `Plan ${i + 1}: Percentages must be between 0 and 100`;
	                }
	                for (let r = 0; r < extraRows.length; r++) {
	                    const row = extraRows[r];
	                    if (!row || typeof row !== 'object') continue;
	                    const label = String(row.label || '').trim();
	                    const percentRaw = row.percent;
	                    const hasPercent = percentRaw !== null && percentRaw !== undefined && String(percentRaw).trim() !== '';
	                    const percent = this.parseNumber(percentRaw);
	                    const hasAny = Boolean(label || hasPercent);
	                    if (!hasAny) continue;
	                    if (!label) return `Plan ${i + 1}: Additional row ${r + 1}: Label is required`;
	                    if (percent === null) return `Plan ${i + 1}: Additional row ${r + 1}: Percent is required`;
	                    if (percent < 0 || percent > 100) return `Plan ${i + 1}: Additional row ${r + 1}: Percent must be between 0 and 100`;
	                }
	                if (!plan.totalPeriod || !plan.paymentCycle || !plan.installmentAmount || !plan.numberOfInstallments) {
	                    return `Plan ${i + 1}: Total Period, Payment Cycle, Installment Amount, and Number of Installments are required`;
	                }
	                const totalPeriod = this.parseInt(plan.totalPeriod);
                if (!totalPeriod || totalPeriod <= 0) {
                    return `Plan ${i + 1}: Total Period must be a positive number`;
                }
                const paymentCycle = this.parseInt(plan.paymentCycle);
                if (!paymentCycle || paymentCycle <= 0) {
                    return `Plan ${i + 1}: Payment Cycle must be a positive number`;
                }
                const numberOfInstallments = this.parseInt(plan.numberOfInstallments);
                if (!numberOfInstallments || numberOfInstallments <= 0) {
                    return `Plan ${i + 1}: Number of Installments must be a positive number`;
                }
                const installmentAmount = this.parseNumber(plan.installmentAmount);
	                if (!installmentAmount || installmentAmount <= 0) {
	                    return `Plan ${i + 1}: Installment Amount must be a positive number`;
	                }
	            }
	        }
        const selectedPriceIssue = this.publicPriceSourceIssue();
        if (selectedPriceIssue) return selectedPriceIssue;
	        return null;
	    }

    private async syncFeatures() {
        if (!this.propertyId) return;
        const existing = this.property?.featureValues || this.property?.features || [];
        const existingFeatureIds = new Set(
            (existing || [])
                .map((feat: any) => feat?.featureId || feat?.FEATURE_ID)
                .filter(Boolean)
                .map((id: any) => String(id))
        );

        for (const featureId of existingFeatureIds) {
            if (!this.selectedFeatures.has(featureId)) {
                await firstValueFrom(this.uploadService.deleteFeatureValue(this.propertyId, featureId).pipe(takeUntil(this.destroy$)));
            }
        }

        for (const [featureId, value] of this.selectedFeatures.entries()) {
            await firstValueFrom(
                this.uploadService.upsertFeatureValue(this.propertyId, featureId, value || 'Yes').pipe(takeUntil(this.destroy$))
            );
        }
    }

    private async syncLandmarks() {
        if (!this.propertyId) return;
        const existing = this.property?.landmarks || [];

        const current = this.landmarks.value as Array<any>;
        const currentIds = new Set(current.map((lm: any) => String(lm.id || '')));

        for (const lm of existing) {
            const id = (lm as any)?.id;
            if (id && !currentIds.has(String(id))) {
                await firstValueFrom(this.uploadService.deleteLandmark(this.propertyId, String(id)).pipe(takeUntil(this.destroy$)));
            }
        }

        for (const lm of current) {
            const presetName = lm.type && lm.type !== 'custom' ? lm.type.toString().trim() : '';
            const name = (lm.name || presetName || '').toString().trim();
            const distanceValue = this.parseNumber(lm.distance);
            const distanceUnit = this.normalizeDistanceUom(lm.distanceUnit);
            const distanceMeter = this.distanceToMeters(distanceValue, distanceUnit);
            if (!name && distanceMeter === null) continue;
            if (lm.id) {
                await firstValueFrom(
                    this.uploadService.updateLandmark(this.propertyId, String(lm.id), {
                        name,
                        distance_meter: distanceMeter,
                        distance_uom: distanceUnit
                    }).pipe(takeUntil(this.destroy$))
                );
            } else {
                await firstValueFrom(
                    this.uploadService.createLandmark(this.propertyId, {
                        name,
                        distance_meter: distanceMeter,
                        distance_uom: distanceUnit
                    }).pipe(takeUntil(this.destroy$))
                );
            }
        }
    }

    private async syncPaymentPlans() {
        if (!this.propertyId) return;
        const existing = this.property?.paymentPlans || [];
        if (this.planMode === 'none') {
            for (const plan of existing) {
                const id = (plan as any)?.id;
                if (id) {
                    await firstValueFrom(this.uploadService.deletePaymentPlan(this.propertyId, String(id)).pipe(takeUntil(this.destroy$)));
                }
            }
            return;
        }

        const current = this.paymentPlans.value as Array<any>;
        const currentIds = new Set(current.filter(p => p.id).map((p: any) => String(p.id)));

        for (const plan of existing) {
            const id = (plan as any)?.id;
            if (id && !currentIds.has(String(id))) {
                await firstValueFrom(this.uploadService.deletePaymentPlan(this.propertyId, String(id)).pipe(takeUntil(this.destroy$)));
            }
        }

        for (const plan of current) {
            const downPercent = this.parseNumber(plan.downPayment);
            const reservePercent = this.parseNumber(plan.reservationAmount);
            // Use plan's OWN displayPrice as base for percent→amount conversion
            const planBasePrice = this.parseNumber(plan.displayPrice) ?? this.getBasePrice();
            const resolveFromPercent = (pct: number | null): number | null => {
                if (!planBasePrice || pct === null || pct === undefined) return null;
                return (planBasePrice * pct) / 100;
            };
            const downAmount = resolveFromPercent(downPercent) ?? this.parseNumber(plan.downPayment);
            const reservationAmount = resolveFromPercent(reservePercent) ?? this.parseNumber(plan.reservationAmount);
            // Auto-recompute installment to ensure components sum to display_price
            const nInst = this.parseInt(plan.numberOfInstallments);
            const displayPrice = this.parseNumber(plan.displayPrice);
            let installmentAmount = this.parseNumber(plan.installmentAmount);
            if (displayPrice !== null && nInst !== null && nInst > 0) {
                const remaining = displayPrice - (reservationAmount ?? 0) - (downAmount ?? 0);
                if (remaining > 0) {
                    installmentAmount = Math.round((remaining / nInst) * 100) / 100;
                }
            }
	            const hasValue = plan.paymentCycle || installmentAmount || nInst || plan.totalPeriod || downPercent !== null || reservePercent !== null || displayPrice !== null;
            if (!hasValue) continue;
            const totalPeriod = this.parseInt(plan.totalPeriod);
            if (!totalPeriod || totalPeriod <= 0) continue;
            if (plan.id) {
                await firstValueFrom(this.uploadService.updatePaymentPlan(this.propertyId, String(plan.id), {
                    total_period: totalPeriod,
                    payment_cycle: this.parseInt(plan.paymentCycle),
                    downpayment: downAmount ?? 0,
	                    reservation_amount: reservationAmount ?? 0,
	                    installment_amount: installmentAmount,
	                    number_of_installments: nInst,
	                    display_price: displayPrice
                }).pipe(takeUntil(this.destroy$)));
            } else {
                await firstValueFrom(this.uploadService.createPaymentPlan(this.propertyId, {
                    total_period: totalPeriod,
                    payment_cycle: this.parseInt(plan.paymentCycle),
                    downpayment: downAmount ?? 0,
	                    reservation_amount: reservationAmount ?? 0,
	                    installment_amount: installmentAmount,
	                    number_of_installments: nInst,
	                    display_price: displayPrice
                }).pipe(takeUntil(this.destroy$)));
            }
        }
    }

    private async syncImageOrdering() {
        if (!this.propertyId || !this.images.length) return;
        this.images = this.assignImageOrder(this.images);
        const updates = this.images
            .map((img, idx) => {
                if (!img.id) return null;
                const label = this.deriveImageLabel(img.alt || img.url, idx);
                return firstValueFrom(
                    this.uploadService.updatePropertyImage(this.propertyId!, String(img.id), {
                        alt: label,
                        sort_order: idx + 1,
                        is_main: Boolean(img.isMain)
                    }).pipe(takeUntil(this.destroy$))
                );
            })
            .filter(Boolean) as Array<Promise<any>>;
        try {
            await Promise.all(updates);
        } catch (err) {
            console.warn('Failed to sync image ordering', err);
            this.notifications.showError('Images saved, but ordering could not be fully updated.').subscribe();
        }
    }

    private getErrorMessage(err: any, fallback: string): string {
        return err?.error?.message || err?.message || fallback;
    }

    onProjectChange() {
        const projectId = this.form.value.projectId;
        if (!projectId) {
            this.phases = [];
            this.form.patchValue({ phaseId: '' });
            this.syncLocationControls(this.useProjectLocation);
            return;
        }
        this.loadingPhases = true;
        const rows = (this.allPhases || []).filter((phase) => String(phase?.project_id || '') === String(projectId));
        this.phases = rows;
        const desired = this.propertyPhaseId || this.form.value.phaseId || '';
        const match = rows.find((p) => p.id === desired);
        this.form.patchValue({ phaseId: match ? match.id : (rows[0]?.id || '') });
        this.propertyPhaseId = null;
        this.loadingPhases = false;
        this.syncLocationControls(this.useProjectLocation);
    }

    setMainImage(image: PropertyImage, index: number) {
        if (!this.propertyId || !image?.url) return;
        this.uploadError = null;
        const imageId = image.id || null;
        if (!imageId) {
            this.uploadError = 'Cannot set main image: missing image id.';
            this.notifications.showError(this.uploadError).subscribe();
            return;
        }
        const request$ = this.uploadService.updatePropertyImage(this.propertyId, String(imageId), { is_main: true });

        request$.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                const updated = this.images.map((img, i) => ({ ...img, isMain: i === index }));
                this.images = this.assignImageOrder(updated);
                this.notifications.showSuccess('Main image updated').subscribe();
            },
            error: (err) => {
                this.uploadError = this.getErrorMessage(err, 'Failed to set main image');
                this.notifications.showError(this.uploadError).subscribe();
            }
        });
    }

	    floorPlanLabel(): string {
	        if (this.uploadingFloorPlan) return 'Uploading…';
	        const fileId = String(this.property?.floorPlanFileId || '').trim();
	        const src = String(this.property?.floorPlanUrl || '').trim();
        if (fileId || src) return 'Floor plan attached';
        return 'No floor plan yet';
    }

    openFloorPlan(event?: Event): void {
        event?.stopPropagation();
        const fileId = String(this.property?.floorPlanFileId || '').trim();
        const src = this.property?.floorPlanUrl || null;
        if (fileId) {
            this.viewerDialog.open({ fileId, title: 'Floor plan' });
            return;
        }
        this.viewerDialog.openFromUrl(src, 'Floor plan');
    }

    async onFloorPlanSelected(event: Event): Promise<void> {
        if (!this.propertyId || this.uploadingFloorPlan) return;
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        this.floorPlanError = null;
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            this.floorPlanError = 'Please upload an image file.';
            this.notifications.showError(this.floorPlanError).subscribe();
            return;
        }

        this.uploadingFloorPlan = true;
        try {
            const uploaded = await firstValueFrom(
                this.uploadService.uploadFile(file, { isPublic: true, propertyId: this.propertyId }).pipe(takeUntil(this.destroy$))
            );
            const fileId = uploaded?.file_id ? String(uploaded.file_id).trim() : '';
            if (!fileId) throw new Error('Upload failed');

            await firstValueFrom(
                this.propertiesService.updateProperty(this.propertyId, { floor_plan_file_id: fileId }).pipe(takeUntil(this.destroy$))
            );

            const meta = await firstValueFrom(this.uploadService.fileMeta(fileId).pipe(takeUntil(this.destroy$)));
            const url = meta?.url ? String(meta.url).trim() : this.uploadService.filePublicUrl(fileId);

            if (this.property) {
                this.property.floorPlanFileId = fileId;
                this.property.floorPlanUrl = url || null;
            }

            this.notifications.showSuccess('Floor plan updated').subscribe();
        } catch (err: any) {
            this.floorPlanError = this.getErrorMessage(err, 'Failed to upload floor plan');
            this.notifications.showError(this.floorPlanError || 'Failed to upload floor plan').subscribe();
        } finally {
            this.uploadingFloorPlan = false;
        }
    }

    async clearFloorPlan(): Promise<void> {
        if (!this.propertyId || this.uploadingFloorPlan) return;
        this.floorPlanError = null;
        this.uploadingFloorPlan = true;
        try {
            await firstValueFrom(
                this.propertiesService.updateProperty(this.propertyId, { floor_plan_file_id: null }).pipe(takeUntil(this.destroy$))
            );
            if (this.property) {
                this.property.floorPlanFileId = null;
                this.property.floorPlanUrl = null;
            }
            this.notifications.showSuccess('Floor plan removed').subscribe();
        } catch (err: any) {
            this.floorPlanError = this.getErrorMessage(err, 'Failed to remove floor plan');
            this.notifications.showError(this.floorPlanError || 'Failed to remove floor plan').subscribe();
        } finally {
            this.uploadingFloorPlan = false;
        }
    }

    layoutImageLabel(): string {
        if (this.uploadingLayoutImage) return 'Uploading…';
        const fileId = String(this.property?.layoutImageFileId || '').trim();
        const src = String(this.property?.layoutImageUrl || '').trim();
        if (fileId || src) return 'Layout image attached';
        return 'No layout image yet';
    }

    openLayoutImage(event?: Event): void {
        event?.stopPropagation();
        const fileId = String(this.property?.layoutImageFileId || '').trim();
        const src = this.property?.layoutImageUrl || null;
        if (fileId) {
            this.viewerDialog.open({ fileId, title: 'Layout image' });
            return;
        }
        this.viewerDialog.openFromUrl(src, 'Layout image');
    }

    async onLayoutImageSelected(event: Event): Promise<void> {
        if (!this.propertyId || this.uploadingLayoutImage) return;
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        this.layoutImageError = null;
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            this.layoutImageError = 'Please upload an image file.';
            this.notifications.showError(this.layoutImageError).subscribe();
            return;
        }

        this.uploadingLayoutImage = true;
        try {
            const uploaded = await firstValueFrom(
                this.uploadService.uploadFile(file, { isPublic: true, propertyId: this.propertyId }).pipe(takeUntil(this.destroy$))
            );
            const fileId = uploaded?.file_id ? String(uploaded.file_id).trim() : '';
            if (!fileId) throw new Error('Upload failed');

            await firstValueFrom(
                this.propertiesService.updateProperty(this.propertyId, { layout_image_file_id: fileId }).pipe(takeUntil(this.destroy$))
            );

            const meta = await firstValueFrom(this.uploadService.fileMeta(fileId).pipe(takeUntil(this.destroy$)));
            const url = meta?.url ? String(meta.url).trim() : this.uploadService.filePublicUrl(fileId);

            if (this.property) {
                this.property.layoutImageFileId = fileId;
                this.property.layoutImageUrl = url || null;
            }

            this.notifications.showSuccess('Layout image updated').subscribe();
        } catch (err: any) {
            this.layoutImageError = this.getErrorMessage(err, 'Failed to upload layout image');
            this.notifications.showError(this.layoutImageError || 'Failed to upload layout image').subscribe();
        } finally {
            this.uploadingLayoutImage = false;
        }
    }

    async clearLayoutImage(): Promise<void> {
        if (!this.propertyId || this.uploadingLayoutImage) return;
        this.layoutImageError = null;
        this.uploadingLayoutImage = true;
        try {
            await firstValueFrom(
                this.propertiesService.updateProperty(this.propertyId, { layout_image_file_id: null }).pipe(takeUntil(this.destroy$))
            );
            if (this.property) {
                this.property.layoutImageFileId = null;
                this.property.layoutImageUrl = null;
            }
            this.notifications.showSuccess('Layout image removed').subscribe();
        } catch (err: any) {
            this.layoutImageError = this.getErrorMessage(err, 'Failed to remove layout image');
            this.notifications.showError(this.layoutImageError || 'Failed to remove layout image').subscribe();
        } finally {
            this.uploadingLayoutImage = false;
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (typeof window !== 'undefined') {
            window.removeEventListener('nibni:media-library-assigned', this.mediaAssignedListener as EventListener);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }
}
